import {
  getRoomMapPreset,
  isRespawnAllowed,
  MAX_GAMEPLAY_LEVEL,
  normalizeGameplayLevel,
  type PrivateRoomConfigValidationError,
  selectableSpectateTargets,
  validatePrivateRoomConfig,
} from "@xiuxian/game-core";
import type {
  JoinIntent,
  PrivateRoomConfig,
  RoomMapSize,
  RoomMember,
  TreasureKind,
} from "@xiuxian/game-protocol";
import Phaser from "phaser";
import {
  createAnalyticsTracker,
  readAnalyticsConfigFromEnv,
} from "./analytics.js";
import {
  formatRealmLayer,
  formatRealmLayerCompact,
} from "./i18n/game-formatters.js";
import {
  formatAscensionCountdownUnit,
  formatAscensionNotice,
  formatAscensionOverlay,
  formatRuntimeLog,
  formatRuntimeStatus,
  formatTreasureGuardBody,
  formatTreasureGuardTitle,
  formatTreasureNoticeIconText,
  formatTreasurePickupTitle,
  formatTreasureSpawnTitle,
  formatWorldRebornOverlay,
} from "./i18n/hud-formatters.js";
import {
  type Locale,
  normalizeLocale,
  resolveLocale,
  translate,
  writeStoredLocale,
} from "./i18n/index.js";
import {
  buildRuntimeLocaleUrl,
  getLocaleSwitcherItems,
  getRuntimeEntryCopy,
} from "./i18n/runtime-entry.js";
import {
  formatScore,
  type PeriodLeaderboardKind,
  renderLeaderboardPanel,
} from "./leaderboard-ui.js";
import {
  createMobileControlsState,
  installMobileControls,
} from "./mobile-controls.js";
import {
  applyMobileRuntimeClasses,
  readMobileRuntimeSnapshot,
} from "./mobile-runtime.js";
import {
  RealtimeClient,
  type RealtimeConnectionConfig,
} from "./realtime-client.js";
import {
  buildInviteUrl,
  isValidInviteCode,
  normalizeInviteCodeInput,
  parseJoinIntentFromSearch,
} from "./room-entry.js";
import { enhanceRoomSelects } from "./room-select.js";
import { readRuntimeConfigFromBrowser } from "./runtime-config.js";
import type { RuntimeViewState } from "./runtime-state.js";
import { RuntimeStateStore } from "./runtime-state.js";
import { RuntimeScene } from "./scenes/runtime-scene.js";
import {
  getSkinDef,
  normalizeSkinId,
  resolveSkinDef,
  SKIN_DEFS,
  type SkinDef,
  type SkinId,
} from "./visual/cultivator-skins.js";
import {
  DEFAULT_TERRAIN_PATTERN,
  normalizeStoredTerrainPattern,
  normalizeTerrainPattern,
  TERRAIN_PATTERN_IDS,
  type TerrainPatternId,
} from "./visual/terrain-patterns.js";
import "./styles.css";
import { createTreasureSlotViews, getTreasureUiConfig } from "./treasure-ui.js";

const PLAYER_NAME_STORAGE_KEY = "xiuxian.runtime.player-name";
const PLAYER_KEY_STORAGE_KEY = "xiuxian.runtime.player-key";
const ANONYMOUS_PLAYER_ID_STORAGE_KEY = "xiuxian.runtime.anonymous-player-id";
const PLAYER_LEVEL_STORAGE_KEY = "xiuxian.runtime.player-level";
const AUDIO_MUTED_STORAGE_KEY = "xiuxian.runtime.audio-muted";
const LEADERBOARD_PERIOD_STORAGE_KEY = "xiuxian.runtime.leaderboard-period";
const PWA_TIP_DISMISSED_STORAGE_KEY = "xiuxian.runtime.pwa-tip-dismissed";
const TERRAIN_STORAGE_KEY = "xiuxian.runtime.terrain";
const SKIN_STORAGE_KEY = "xiuxian.runtime.skin";
const LEADERBOARD_COLLAPSE_KEY = "xiuxian.runtime.leaderboardCollapsed";
const TERRAIN_LABEL_KEYS: Record<
  TerrainPatternId,
  Parameters<typeof translate>[1]
> = {
  grid: "identity.terrainGrid",
  hex: "identity.terrainHex",
  dot: "identity.terrainDot",
};
const SKIN_PRESENTATION: Record<
  SkinId,
  {
    tag: Record<Locale, string>;
    motto: Record<Locale, string>;
    description: Record<Locale, string>;
  }
> = {
  qingyun: {
    tag: { "zh-CN": "剑修", en: "Sword" },
    motto: { "zh-CN": "御剑如风", en: "Swordwind Step" },
    description: {
      "zh-CN": "玉冠青袍，剑意清亮，入界第一眼就是正派主角。",
      en: "A jade-crowned sword cultivator with a clean heroic silhouette.",
    },
  },
  moyu: {
    tag: { "zh-CN": "符修", en: "Talisman" },
    motto: { "zh-CN": "符箓随行", en: "Scrolls in Motion" },
    description: {
      "zh-CN": "墨衫银纹，卷轴在背，把战场写成一张阵图。",
      en: "Dark robes and scrolls turn every route into a drawn formation.",
    },
  },
  danxia: {
    tag: { "zh-CN": "丹修", en: "Alchemy" },
    motto: { "zh-CN": "葫火炼灵", en: "Gourdfire Spirit" },
    description: {
      "zh-CN": "红金衣袂与小葫芦，暖色系最有活力。",
      en: "Warm red-gold robes and a little gourd make the liveliest look.",
    },
  },
  xuanshuang: {
    tag: { "zh-CN": "冰修", en: "Frost" },
    motto: { "zh-CN": "霜华护身", en: "Frostguard Robe" },
    description: {
      "zh-CN": "霜白长氅与冷蓝灵气，清冷但很醒目。",
      en: "Frost-white robes and cold-blue spirit light for a crisp profile.",
    },
  },
  zifu: {
    tag: { "zh-CN": "雷修", en: "Thunder" },
    motto: { "zh-CN": "雷纹高冠", en: "Crown of Thunder" },
    description: {
      "zh-CN": "紫金高冠气场最强，适合想要威仪感的玩家。",
      en: "Purple-gold thunder regalia with the strongest ceremonial presence.",
    },
  },
  linglong: {
    tag: { "zh-CN": "妖修", en: "Fox" },
    motto: { "zh-CN": "狐影灵动", en: "Foxshadow Dash" },
    description: {
      "zh-CN": "狐耳与橙红灵气，俏皮又有 Q 版辨识度。",
      en: "Fox ears and warm orange spirit light for the most playful silhouette.",
    },
  },
};
const PLAYER_NAME_MAX_LENGTH = 24;
// Appearance dialog: fixed design-canvas size + max scale. Declared up here so
// they're initialized before the bootstrap flow (which reaches the appearance
// UI synchronously at load) reads them — avoids a temporal-dead-zone error.
const APPEARANCE_DESIGN_WIDTH = 900;
const APPEARANCE_DESIGN_HEIGHT = 676;
const APPEARANCE_MAX_SCALE = 1.25;
// Small-world (room) dialog uses the same fixed-canvas + transform-scale trick;
// canvas matches the frame image aspect 1063:1442.
const ROOM_PANEL_DESIGN_WIDTH = 620;
const ROOM_PANEL_DESIGN_HEIGHT = 841;
const ROOM_PANEL_MAX_SCALE = 1.15;
// Framed (solo) waiting room: fixed 560px-wide canvas, height per player count
// (matches the three lobby backgrounds 1039×{1229,1346,1461}).
const WAITING_DESIGN_WIDTH = 560;
const WAITING_MAX_SCALE = 1.1;
const WAITING_DESIGN_HEIGHT: Record<string, number> = {
  "count-1": 662,
  "count-2": 725,
  "count-3plus": 787,
};
// Framed match-result dialog: fixed canvas matching the frame art (1110×1144).
const MATCH_RESULT_DESIGN_WIDTH = 540;
const MATCH_RESULT_DESIGN_HEIGHT = 557;
const MATCH_RESULT_MAX_SCALE = 1.1;
// Framed settings dialog: fixed canvas matching the frame art (1394×965).
const SETTINGS_DESIGN_WIDTH = 680;
const SETTINGS_DESIGN_HEIGHT = 471;
const SETTINGS_MAX_SCALE = 1.1;
// Player-limit preset tiers (filtered to the map cap at runtime). BOT count is
// a free 0..(limit-2) range, so it has no fixed preset list.
const PLAYER_LIMIT_OPTIONS = [2, 4, 8, 12, 16, 30] as const;
// Common names offered when a player starts without entering one, so a
// first-timer can just tap "踏入仙途" and get dropped in with a fitting name.
const RANDOM_PLAYER_NAMES: Record<string, readonly string[]> = {
  "zh-CN": [
    "青岚",
    "墨白",
    "苏澜",
    "凌霄",
    "云舒",
    "玄阳",
    "白芷",
    "洛尘",
    "疏影",
    "临渊",
    "烟波",
    "寒山",
    "清越",
    "暮雪",
    "星河",
    "无痕",
    "听雪",
    "南屿",
    "司命",
    "羽衣",
  ],
  en: [
    "Ashen",
    "Lyra",
    "Orion",
    "Wren",
    "Sable",
    "Rowan",
    "Nova",
    "Kai",
    "Vesper",
    "Cyra",
    "Zephyr",
    "Aria",
    "Cael",
    "Iris",
    "Onyx",
    "Lior",
  ],
};
const DEFAULT_PLAYER_LEVEL = 1;
// The dev starting-level selector is a development-only aid: production builds
// never show it. Within dev mode, `VITE_GAME_DEV_LEVEL_SELECTOR`
// ("0"/"false"/"off") can turn it off; unset means on.
const DEV_LEVEL_SELECTOR_ENABLED = readDevLevelSelectorEnabled();

function readDevLevelSelectorEnabled(): boolean {
  if (!import.meta.env.DEV) {
    return false;
  }
  const raw = (
    import.meta.env as Record<string, string | undefined>
  ).VITE_GAME_DEV_LEVEL_SELECTOR?.trim();
  if (raw === undefined || raw === "") {
    return true;
  }
  const normalized = raw.toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "on";
}
const CLOSE_MOBILE_LEADERBOARD_EVENT = "xiuxian:close-mobile-leaderboard";
const LOCALE_CHANGED_EVENT = "xiuxian:locale-changed";
const DEATH_SPECTATE_HINT_VISIBLE_MS = 4200;
type IdentityPromptMode = "start" | "restart";
type BeforeInstallPromptOutcome = "accepted" | "dismissed";
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: BeforeInstallPromptOutcome;
    platform: string;
  }>;
};

const runtimeConfig = readRuntimeConfigFromBrowser();
const analytics = createAnalyticsTracker(
  readAnalyticsConfigFromEnv({
    VITE_ANALYTICS_ENDPOINT: import.meta.env.VITE_ANALYTICS_ENDPOINT,
    VITE_ANALYTICS_DEBUG: import.meta.env.VITE_ANALYTICS_DEBUG,
  }),
);
let currentLocale: Locale = resolveLocale({ currentUrl: window.location.href });
const runtimeStore = new RuntimeStateStore(currentLocale);
const realtimeClient = new RealtimeClient(runtimeStore, () => currentLocale);
const mobileControls = createMobileControlsState();
let identityPromptActive = false;
/** Auto-respawn delay after death in private timed-score rooms (ms). */
const RESPAWN_COUNTDOWN_MS = 3000;
let promptedDeathPlayerId: string | null = null;
let spectatingDeathPlayerId: string | null = null;
let deathRestartPromptPending = false;
let gameInstance: Phaser.Game | null = null;
let runtimeScene: RuntimeScene | null = null;
let audioMuted = readStoredAudioMuted();
let currentTerrain: TerrainPatternId = readStoredTerrain();
let currentSkin: SkinId = readStoredSkin();
let selectedPeriod: PeriodLeaderboardKind = readStoredLeaderboardPeriod();
let currentIdentityPromptMode: IdentityPromptMode = "start";
let mouseLockActive = false;
// The room the next connection should land in. Seeded from the URL, then
// overridden by the entry dialog's create / join actions.
let pendingJoinIntent: JoinIntent = parseJoinIntentFromSearch(
  window.location.search,
);

if (runtimeConfig.embedded) {
  document.body.classList.add("is-hosted-runtime");
}
applyRuntimeEntryLocale();
installLocaleSwitcher();
installMobileRuntimeBasics();
installMobileControlUi();
document.body.classList.add("runtime-ready");

installHud(runtimeStore);
installTreasureHudAndNotifications(runtimeStore);
installAscensionRitualOverlay(runtimeStore);
installWaitingRoom(runtimeStore);
installMatchHud(runtimeStore);
installSpectatorBar(runtimeStore);
installChatPanel(runtimeStore);
installRoomUnavailable(runtimeStore);
installDeathRestartPrompt(runtimeStore);
installDebugHandle();
bootstrapGame();
installViewportSync(runtimeStore);
installPwaEnhancements();
installSoundToggle();
installSettingsDialog();
installRoomPanel();
installMouseLockToggle();
analytics.installRuntimeTracking((listener) =>
  runtimeStore.subscribe(listener),
);
void initializeRuntime();

window.addEventListener("beforeunload", () => {
  analytics.flushSessionEnd("beforeunload", runtimeStore.getState());
  realtimeClient.disconnect();
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    analytics.flushSessionEnd("visibility_hidden", runtimeStore.getState());
  }
});

function tr(
  key: Parameters<typeof translate>[1],
  params?: Parameters<typeof translate>[2],
): string {
  return translate(currentLocale, key, params);
}

function installLocaleSwitcher(): void {
  const switcher = document.querySelector<HTMLElement>("#locale-switcher");
  if (!switcher) {
    return;
  }

  stopGameInputPropagation(switcher);
  switcher.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const button = target.closest<HTMLButtonElement>("[data-locale]");
    const locale = normalizeLocale(button?.dataset.locale);
    if (!button || !locale) {
      return;
    }

    event.preventDefault();
    if (locale === currentLocale) {
      return;
    }
    if (
      !runtimeConfig.embedded &&
      !document.body.classList.contains("is-gameplay-active")
    ) {
      navigateRuntimeLocale(locale);
      return;
    }
    setRuntimeLocale(locale);
  });
  updateLocaleSwitcher(switcher);
}

function navigateRuntimeLocale(locale: Locale): void {
  try {
    writeStoredLocale(window.localStorage, locale);
  } catch {
    writeStoredLocale(null, locale);
  }
  window.location.assign(buildRuntimeLocaleUrl(window.location.href, locale));
}

function setRuntimeLocale(locale: Locale): void {
  if (locale === currentLocale) {
    return;
  }

  currentLocale = locale;
  try {
    writeStoredLocale(window.localStorage, locale);
  } catch {
    writeStoredLocale(null, locale);
  }
  applyRuntimeEntryLocale();
  document.dispatchEvent(new Event(LOCALE_CHANGED_EVENT));
  refreshHudForIdentityPrompt();
}

function applyRuntimeEntryLocale(): void {
  const copy = getRuntimeEntryCopy(currentLocale, currentIdentityPromptMode, {
    fullscreenActive: isFullscreenActive(),
    installPromptAvailable: false,
  });
  document.documentElement.lang = copy.htmlLang;
  document.title = copy.documentTitle;
  document
    .querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')
    ?.setAttribute("content", copy.appTitle);

  setTextContent("#mobile-boost", copy.controls.mobileBoost);
  setTextContent(".leaderboard-title-text", copy.leaderboard.title);
  setTextContent("#mobile-leaderboard-toggle", copy.leaderboard.mobileToggle);
  setTextContent(".identity-label", copy.identity.label);
  setInputPlaceholder("#player-name-input", copy.identity.placeholder);
  setTextContent("#identity-submit", copy.identity.submit);
  setTextContent("[data-room-caption]", tr("identity.roomCardLabel"));
  updateDevLevelLabel();
  setTextContent("#mobile-pwa-install", copy.pwa.install);
  setTextContent("#mobile-fullscreen-toggle", copy.pwa.fullscreen);
  setTextContent("#mobile-pwa-tip-text", copy.pwa.tip);
  document
    .querySelector<HTMLButtonElement>("#mobile-pwa-dismiss")
    ?.setAttribute("aria-label", copy.pwa.dismissLabel);

  const leaderboard = document.querySelector<HTMLElement>("#leaderboard-list");
  if (leaderboard && leaderboard.children.length === 0) {
    leaderboard.textContent = copy.leaderboard.empty;
  }
  const soundButton =
    document.querySelector<HTMLButtonElement>("#sound-toggle");
  if (soundButton) {
    updateSoundToggle(soundButton);
  }
  const mouseLockButton =
    document.querySelector<HTMLButtonElement>("#mouse-lock-toggle");
  if (mouseLockButton) {
    updateMouseLockToggle(mouseLockButton, mouseLockActive);
  }
  const switcher = document.querySelector<HTMLElement>("#locale-switcher");
  if (switcher) {
    switcher.setAttribute("aria-label", copy.localeLabel);
    updateLocaleSwitcher(switcher);
  }
  updateIdentityContentLinks(copy.contentLinks);
  syncAppearanceUi();
}

function updateIdentityContentLinks(
  links: ReturnType<typeof getRuntimeEntryCopy>["contentLinks"],
): void {
  const nav = document.querySelector<HTMLElement>(".identity-links");
  if (!nav) {
    return;
  }
  nav.setAttribute("aria-label", links.label);
  for (const key of ["library", "privacy", "feedback", "discord"] as const) {
    const anchor = document.querySelector<HTMLAnchorElement>(
      `[data-content-link="${key}"]`,
    );
    if (!anchor) {
      continue;
    }
    // Pedestal footer cards: icon on top, localized caption on the bottom.
    anchor.setAttribute("aria-label", links[key].label);
    anchor.setAttribute("title", links[key].label);
    anchor.setAttribute("href", links[key].href);
    // Open the guide / privacy / feedback / Discord in a new tab so the game
    // session isn't navigated away from.
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener");
    const caption = anchor.querySelector<HTMLElement>(
      "[data-content-link-caption]",
    );
    if (caption) {
      caption.textContent = links[key].label;
    }
  }
}

function readStoredTerrain(): TerrainPatternId {
  try {
    const stored = window.localStorage.getItem(TERRAIN_STORAGE_KEY);
    const normalized = normalizeStoredTerrainPattern(stored);
    if (normalized.shouldPersist) {
      window.localStorage.setItem(TERRAIN_STORAGE_KEY, normalized.id);
    }
    return normalized.id;
  } catch {
    return DEFAULT_TERRAIN_PATTERN;
  }
}

function writeStoredTerrain(id: TerrainPatternId): void {
  try {
    window.localStorage.setItem(TERRAIN_STORAGE_KEY, id);
  } catch {
    // Local preference only; ignore storage failures.
  }
}

function readStoredSkin(): SkinId {
  try {
    return normalizeSkinId(window.localStorage.getItem(SKIN_STORAGE_KEY));
  } catch {
    return normalizeSkinId(undefined);
  }
}

function writeStoredSkin(id: SkinId): void {
  try {
    window.localStorage.setItem(SKIN_STORAGE_KEY, id);
  } catch {
    // Local preference only; ignore storage failures.
  }
}

function readStoredLeaderboardCollapsed(): boolean {
  try {
    return window.localStorage.getItem(LEADERBOARD_COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeStoredLeaderboardCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(
      LEADERBOARD_COLLAPSE_KEY,
      collapsed ? "1" : "0",
    );
  } catch {
    // Local preference only; ignore storage failures.
  }
}

/** Show frame 0 of a skin's top-down sheet as a CSS background thumbnail. */
function applySkinThumb(element: HTMLElement, def: SkinDef): void {
  element.style.backgroundImage = `url("${def.path}")`;
  element.style.backgroundRepeat = "no-repeat";
  element.style.backgroundPosition = "center top";
  element.style.backgroundSize = `${(def.frameCount * 100).toString()}% 100%`;
}

/** Stable non-negative hash of a player id, for skinless fallback picks. */
function skinSeedFromId(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return hash;
}

/** Prefer the full-body portrait for UI cards; fall back to the top-down sheet. */
function applySkinPortrait(element: HTMLElement, def: SkinDef): void {
  if (!def.portraitPath) {
    applySkinThumb(element, def);
    return;
  }
  element.style.backgroundImage = `url("${def.portraitPath}")`;
  element.style.backgroundRepeat = "no-repeat";
  element.style.backgroundPosition = "center bottom";
  element.style.backgroundSize = "contain";
}

function setCurrentSkin(id: SkinId): void {
  currentSkin = normalizeSkinId(id);
  writeStoredSkin(currentSkin);
  runtimeScene?.setSelfSkin(currentSkin);
  syncAppearanceUi();
}

function paintHexOutline(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  s: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i += 1) {
    const angle = (Math.PI / 3) * i;
    const x = cx + s * Math.cos(angle);
    const y = cy + s * Math.sin(angle);
    if (i === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.closePath();
  ctx.stroke();
}

/** Paint a small preview of a terrain pattern into a swatch canvas. */
function paintTerrainSwatch(
  canvas: HTMLCanvasElement,
  id: TerrainPatternId,
  css = 40,
): void {
  const dpr = Math.min(3, Math.max(1, window.devicePixelRatio || 1));
  canvas.width = css * dpr;
  canvas.height = css * dpr;
  canvas.style.width = `${css.toString()}px`;
  canvas.style.height = `${css.toString()}px`;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  ctx.scale(dpr, dpr);
  ctx.fillStyle = "#0a1622";
  ctx.fillRect(0, 0, css, css);
  const line = "rgba(131,176,198,0.62)";
  ctx.strokeStyle = line;
  ctx.fillStyle = line;
  ctx.lineWidth = 1;
  if (id === "grid") {
    for (let x = 6; x < css; x += 9) {
      ctx.beginPath();
      ctx.moveTo(x + 0.5, 2);
      ctx.lineTo(x + 0.5, css - 2);
      ctx.stroke();
    }
    for (let y = 6; y < css; y += 9) {
      ctx.beginPath();
      ctx.moveTo(2, y + 0.5);
      ctx.lineTo(css - 2, y + 0.5);
      ctx.stroke();
    }
  } else if (id === "dot") {
    for (let x = 7; x < css; x += 9) {
      for (let y = 7; y < css; y += 9) {
        ctx.beginPath();
        ctx.arc(x, y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else {
    const s = 8;
    const rowStep = Math.sqrt(3) * s;
    for (let col = 0; col <= 3; col += 1) {
      const cx = 1.5 * s * col - 6;
      const yOffset = (((col % 2) + 2) % 2) * (rowStep / 2);
      for (let row = -1; row <= 3; row += 1) {
        paintHexOutline(ctx, cx, rowStep * row + yOffset, s);
      }
    }
  }
}

function setCurrentTerrain(id: TerrainPatternId): void {
  currentTerrain = normalizeTerrainPattern(id);
  writeStoredTerrain(currentTerrain);
  runtimeScene?.setTerrainPattern(currentTerrain);
  syncAppearanceUi();
}

function handleAppearanceKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") {
    event.preventDefault();
    closeAppearanceDialog();
  }
}

function updateAppearanceScale(): void {
  const card = document.querySelector<HTMLElement>(".appearance-card");
  if (!card) {
    return;
  }
  const margin = 32;
  const scale = Math.min(
    (window.innerWidth - margin) / APPEARANCE_DESIGN_WIDTH,
    (window.innerHeight - margin) / APPEARANCE_DESIGN_HEIGHT,
    APPEARANCE_MAX_SCALE,
  );
  card.style.setProperty("--appearance-scale", scale.toFixed(4));
}

function updateRoomPanelScale(): void {
  const card = document.querySelector<HTMLElement>(".room-panel-card");
  if (!card) {
    return;
  }
  const margin = 32;
  const scale = Math.min(
    (window.innerWidth - margin) / ROOM_PANEL_DESIGN_WIDTH,
    (window.innerHeight - margin) / ROOM_PANEL_DESIGN_HEIGHT,
    ROOM_PANEL_MAX_SCALE,
  );
  card.style.setProperty("--room-panel-scale", scale.toFixed(4));
}

function updateWaitingRoomScale(): void {
  const card = document.querySelector<HTMLElement>(".waiting-card.is-framed");
  if (!card) {
    return;
  }
  const countClass =
    (["count-1", "count-2", "count-3plus"] as const).find((c) =>
      card.classList.contains(c),
    ) ?? "count-3plus";
  const designHeight = WAITING_DESIGN_HEIGHT[countClass] ?? 787;
  const margin = 32;
  const scale = Math.min(
    (window.innerWidth - margin) / WAITING_DESIGN_WIDTH,
    (window.innerHeight - margin) / designHeight,
    WAITING_MAX_SCALE,
  );
  card.style.setProperty("--waiting-scale", scale.toFixed(4));
}

function updateMatchResultScale(): void {
  const card = document.querySelector<HTMLElement>(".match-result-card");
  if (!card) {
    return;
  }
  const margin = 32;
  const scale = Math.min(
    (window.innerWidth - margin) / MATCH_RESULT_DESIGN_WIDTH,
    (window.innerHeight - margin) / MATCH_RESULT_DESIGN_HEIGHT,
    MATCH_RESULT_MAX_SCALE,
  );
  card.style.setProperty("--match-result-scale", scale.toFixed(4));
}

function updateSettingsScale(): void {
  const card = document.querySelector<HTMLElement>(".settings-card");
  if (!card) {
    return;
  }
  const margin = 32;
  const scale = Math.min(
    (window.innerWidth - margin) / SETTINGS_DESIGN_WIDTH,
    (window.innerHeight - margin) / SETTINGS_DESIGN_HEIGHT,
    SETTINGS_MAX_SCALE,
  );
  card.style.setProperty("--settings-scale", scale.toFixed(4));
}

function openAppearanceDialog(): void {
  const dialog = document.querySelector<HTMLElement>("#appearance-dialog");
  if (!dialog) {
    return;
  }
  syncAppearanceUi();
  updateAppearanceScale();
  dialog.classList.remove("is-hidden");
  document.addEventListener("keydown", handleAppearanceKeydown);
}

function closeAppearanceDialog(): void {
  document
    .querySelector<HTMLElement>("#appearance-dialog")
    ?.classList.add("is-hidden");
  document.removeEventListener("keydown", handleAppearanceKeydown);
}

function createAppearanceBody(): HTMLDivElement {
  const body = document.createElement("div");
  body.className = "appearance-body";
  body.append(createAppearanceStage(), createAppearanceChoices());
  return body;
}

function createAppearanceStage(): HTMLDivElement {
  const stage = document.createElement("div");
  stage.className = "appearance-stage";
  const stageBadge = document.createElement("div");
  stageBadge.className = "appearance-stage-badge";
  stageBadge.dataset.skinStageTag = "1";
  const skinPreview = document.createElement("div");
  skinPreview.className = "appearance-skin-preview appearance-stage-portrait";
  skinPreview.dataset.skinPreview = "1";
  const terrainPreview = document.createElement("canvas");
  terrainPreview.className = "appearance-stage-terrain";
  terrainPreview.dataset.stageTerrainPreview = "1";
  const topdownWrap = document.createElement("div");
  topdownWrap.className = "appearance-stage-topdown-wrap";
  const topdownLabel = document.createElement("span");
  topdownLabel.className = "appearance-stage-topdown-label";
  topdownLabel.dataset.skinStageShapeLabel = "1";
  const topdownPreview = document.createElement("span");
  topdownPreview.className = "appearance-stage-topdown";
  topdownPreview.dataset.skinTopdownPreview = "1";
  topdownWrap.append(topdownLabel, topdownPreview);
  const stageCopy = document.createElement("div");
  stageCopy.className = "appearance-stage-copy";
  const stageName = document.createElement("h3");
  stageName.dataset.skinStageName = "1";
  const stageMotto = document.createElement("p");
  stageMotto.dataset.skinStageMotto = "1";
  const stageDescription = document.createElement("small");
  stageDescription.dataset.skinStageDescription = "1";
  stageCopy.append(stageName, stageMotto, stageDescription);
  stage.append(stageBadge, terrainPreview, skinPreview, topdownWrap, stageCopy);
  return stage;
}

function createAppearanceChoices(): HTMLDivElement {
  const choices = document.createElement("div");
  choices.className = "appearance-choices";
  choices.append(createSkinChoiceSection(), createTerrainChoiceSection());
  return choices;
}

function createSkinChoiceSection(): HTMLDivElement {
  const skinSection = document.createElement("div");
  skinSection.className = "appearance-section appearance-skin-section";
  const skinLabel = document.createElement("div");
  skinLabel.className = "appearance-section-label";
  skinLabel.dataset.appearanceSkinLabel = "1";
  skinSection.appendChild(skinLabel);
  const skinGrid = document.createElement("div");
  skinGrid.className = "appearance-skin-grid";
  for (const def of SKIN_DEFS) {
    skinGrid.appendChild(createSkinChoiceCard(def));
  }
  skinSection.appendChild(skinGrid);
  return skinSection;
}

function createSkinChoiceCard(def: SkinDef): HTMLButtonElement {
  const swatch = document.createElement("button");
  swatch.type = "button";
  swatch.className = "appearance-swatch appearance-skin-card";
  swatch.dataset.skin = def.id;
  swatch.style.setProperty("--skin-accent", def.swatchColor);
  const thumb = document.createElement("span");
  thumb.className = "appearance-swatch-thumb appearance-skin-card-thumb";
  applySkinPortrait(thumb, def);
  const copy = document.createElement("span");
  copy.className = "appearance-skin-card-copy";
  const label = document.createElement("strong");
  label.className = "appearance-swatch-label";
  label.dataset.skinLabel = def.id;
  const tag = document.createElement("span");
  tag.className = "appearance-skin-card-tag";
  tag.dataset.skinCardTag = def.id;
  const motto = document.createElement("small");
  motto.className = "appearance-skin-card-motto";
  motto.dataset.skinCardMotto = def.id;
  // Name + motto on the left; the 流派 tag lives in its own right column.
  copy.append(label, motto);
  const seal = document.createElement("span");
  seal.className = "appearance-selected-seal";
  seal.dataset.skinSelectedSeal = def.id;
  swatch.append(thumb, copy, tag, seal);
  swatch.addEventListener("click", () => {
    setCurrentSkin(def.id);
  });
  return swatch;
}

function createTerrainChoiceSection(): HTMLDivElement {
  const terrainSection = document.createElement("div");
  terrainSection.className = "appearance-section appearance-terrain-section";
  const terrainLabel = document.createElement("div");
  terrainLabel.className = "appearance-section-label";
  terrainLabel.dataset.appearanceTerrainLabel = "1";
  terrainSection.appendChild(terrainLabel);
  const terrainGrid = document.createElement("div");
  terrainGrid.className = "appearance-terrain-grid";
  for (const id of TERRAIN_PATTERN_IDS) {
    terrainGrid.appendChild(createTerrainChoiceCard(id));
  }
  terrainSection.appendChild(terrainGrid);
  return terrainSection;
}

function createTerrainChoiceCard(id: TerrainPatternId): HTMLButtonElement {
  const swatch = document.createElement("button");
  swatch.type = "button";
  swatch.className = "appearance-swatch appearance-terrain-card";
  swatch.dataset.terrain = id;
  const img = document.createElement("img");
  img.className = "appearance-swatch-img";
  img.src = `/images/terrain-${id}.webp`;
  img.alt = "";
  img.setAttribute("aria-hidden", "true");
  const label = document.createElement("span");
  label.className = "appearance-swatch-label";
  label.dataset.terrainLabel = id;
  swatch.append(img, label);
  swatch.addEventListener("click", () => {
    setCurrentTerrain(id);
  });
  return swatch;
}

/** Inject the appearance icon button (after the name input) and its dialog. */
function ensureAppearanceUi(form: HTMLFormElement): void {
  if (form.querySelector("#appearance-toggle")) {
    return;
  }
  const input = form.querySelector<HTMLInputElement>("#player-name-input");
  if (!input) {
    return;
  }

  const toggle = document.createElement("button");
  toggle.id = "appearance-toggle";
  toggle.type = "button";
  toggle.className = "identity-appearance-toggle";
  const toggleThumb = document.createElement("span");
  toggleThumb.className = "appearance-toggle-thumb";
  const toggleAction = document.createElement("span");
  toggleAction.className = "appearance-toggle-action";
  toggleAction.dataset.appearanceToggleAction = "1";
  toggle.append(toggleThumb, toggleAction);
  toggle.addEventListener("click", openAppearanceDialog);
  stopGameInputPropagation(toggle);
  // The de-emphasized entry row: name input + skin button + room button.
  const row = document.createElement("div");
  row.className = "identity-name-row";
  input.parentNode?.insertBefore(row, input);
  row.appendChild(input);
  row.appendChild(toggle);
  const roomToggle =
    form.querySelector<HTMLButtonElement>("#room-entry-toggle");
  if (roomToggle) {
    row.appendChild(roomToggle);
  }

  const dialog = document.createElement("div");
  dialog.id = "appearance-dialog";
  dialog.className = "appearance-dialog is-hidden";
  dialog.setAttribute("role", "dialog");
  dialog.setAttribute("aria-modal", "true");
  dialog.setAttribute("aria-labelledby", "appearance-title");
  const card = document.createElement("div");
  card.className = "appearance-card";
  const title = document.createElement("h2");
  title.id = "appearance-title";
  title.className = "appearance-title";
  title.dataset.appearanceTitle = "1";
  card.appendChild(title);
  const subtitle = document.createElement("p");
  subtitle.className = "appearance-subtitle";
  subtitle.dataset.appearanceSubtitle = "1";
  card.appendChild(subtitle);
  card.appendChild(createAppearanceBody());

  const close = document.createElement("button");
  close.type = "button";
  close.className = "appearance-close";
  close.dataset.appearanceCloseBtn = "1";
  close.addEventListener("click", closeAppearanceDialog);
  card.appendChild(close);

  // The confirm button straddles the frame's bottom edge (inside the scaled
  // canvas so it scales with everything).
  const done = document.createElement("button");
  done.type = "button";
  done.className = "appearance-done button-like";
  done.dataset.appearanceDone = "1";
  done.addEventListener("click", closeAppearanceDialog);
  card.appendChild(done);

  dialog.appendChild(card);
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) {
      closeAppearanceDialog();
    }
  });
  stopGameInputPropagation(dialog);
  document.body.appendChild(dialog);
  window.addEventListener("resize", updateAppearanceScale);
  updateAppearanceScale();
}

/** Refresh appearance labels (i18n) and selected-state highlighting. */
function syncAppearanceUi(): void {
  const title = document.querySelector<HTMLElement>("[data-appearance-title]");
  if (title) {
    title.textContent = tr("identity.appearanceTitle");
  }
  setTextContent(
    "[data-appearance-subtitle]",
    tr("identity.appearanceSubtitle"),
  );
  const closeBtn = document.querySelector<HTMLElement>(
    "[data-appearance-close-btn]",
  );
  if (closeBtn) {
    closeBtn.setAttribute("aria-label", tr("settings.close"));
    closeBtn.setAttribute("title", tr("settings.close"));
  }
  const done = document.querySelector<HTMLElement>("[data-appearance-done]");
  if (done) {
    done.textContent = tr("identity.appearanceDone");
  }
  const terrainLabel = document.querySelector<HTMLElement>(
    "[data-appearance-terrain-label]",
  );
  if (terrainLabel) {
    terrainLabel.textContent = tr("identity.terrain");
  }
  const skinLabel = document.querySelector<HTMLElement>(
    "[data-appearance-skin-label]",
  );
  if (skinLabel) {
    skinLabel.textContent = tr("identity.skin");
  }
  document
    .querySelector<HTMLElement>("#appearance-toggle")
    ?.setAttribute("aria-label", tr("identity.appearance"));
  const selectedSkin = getSkinDef(currentSkin);
  if (selectedSkin) {
    const selectedPresentation = SKIN_PRESENTATION[selectedSkin.id];
    setTextContent(
      "[data-appearance-toggle-action]",
      tr("identity.skinCardLabel"),
    );
    setTextContent(
      "[data-skin-stage-tag]",
      selectedPresentation.tag[currentLocale],
    );
    setTextContent(
      "[data-skin-stage-shape-label]",
      tr("identity.skinStageShape"),
    );
    setTextContent("[data-skin-stage-name]", selectedSkin.label[currentLocale]);
    setTextContent(
      "[data-skin-stage-motto]",
      selectedPresentation.motto[currentLocale],
    );
    setTextContent(
      "[data-skin-stage-description]",
      selectedPresentation.description[currentLocale],
    );
    const stageTerrain = document.querySelector<HTMLCanvasElement>(
      "[data-stage-terrain-preview]",
    );
    if (stageTerrain) {
      paintTerrainSwatch(stageTerrain, currentTerrain, 132);
    }
  }
  for (const id of TERRAIN_PATTERN_IDS) {
    const label = document.querySelector<HTMLElement>(
      `[data-terrain-label="${id}"]`,
    );
    const terrainName = tr(TERRAIN_LABEL_KEYS[id]);
    if (label) {
      label.textContent = terrainName;
    }
    const swatch = document.querySelector<HTMLElement>(
      `.appearance-swatch[data-terrain="${id}"]`,
    );
    swatch?.classList.toggle("is-active", id === currentTerrain);
    swatch?.setAttribute("aria-pressed", (id === currentTerrain).toString());
    swatch?.setAttribute("aria-label", terrainName);
  }
  for (const def of SKIN_DEFS) {
    const presentation = SKIN_PRESENTATION[def.id];
    const skinName = def.label[currentLocale];
    const label = document.querySelector<HTMLElement>(
      `[data-skin-label="${def.id}"]`,
    );
    if (label) {
      label.textContent = skinName;
    }
    setTextContent(
      `[data-skin-card-tag="${def.id}"]`,
      presentation.tag[currentLocale],
    );
    setTextContent(
      `[data-skin-card-motto="${def.id}"]`,
      presentation.motto[currentLocale],
    );
    setTextContent(
      `[data-skin-selected-seal="${def.id}"]`,
      tr("identity.appearanceSelected"),
    );
    const swatch = document.querySelector<HTMLElement>(
      `.appearance-swatch[data-skin="${def.id}"]`,
    );
    swatch?.classList.toggle("is-active", def.id === currentSkin);
    swatch?.setAttribute("aria-pressed", (def.id === currentSkin).toString());
    swatch?.setAttribute(
      "aria-label",
      `${skinName} ${presentation.motto[currentLocale]}`,
    );
  }
  const skinPreview = document.querySelector<HTMLElement>(
    "[data-skin-preview]",
  );
  if (skinPreview && selectedSkin) {
    applySkinPortrait(skinPreview, selectedSkin);
  }
  const toggleThumb = document.querySelector<HTMLElement>(
    ".appearance-toggle-thumb",
  );
  if (toggleThumb && selectedSkin) {
    applySkinPortrait(toggleThumb, selectedSkin);
  }
  const topdownPreview = document.querySelector<HTMLElement>(
    "[data-skin-topdown-preview]",
  );
  if (topdownPreview && selectedSkin) {
    applySkinThumb(topdownPreview, selectedSkin);
  }
}

function updateLocaleSwitcher(switcher: HTMLElement): void {
  for (const item of getLocaleSwitcherItems(currentLocale)) {
    const button = switcher.querySelector<HTMLButtonElement>(
      `[data-locale="${item.locale}"]`,
    );
    if (!button) {
      continue;
    }
    button.textContent = item.label;
    button.setAttribute("aria-pressed", item.active.toString());
    button.classList.toggle("is-active", item.active);
  }
}

function setTextContent(selector: string, value: string): void {
  const element = document.querySelector<HTMLElement>(selector);
  if (element) {
    element.textContent = value;
  }
}

function setInputPlaceholder(selector: string, value: string): void {
  const element = document.querySelector<HTMLInputElement>(selector);
  if (element) {
    element.placeholder = value;
  }
}

function updateDevLevelLabel(): void {
  const field = document.querySelector<HTMLLabelElement>(".identity-dev-level");
  if (!field) {
    return;
  }
  const textNode = [...field.childNodes].find(
    (node) => node.nodeType === Node.TEXT_NODE,
  );
  if (textNode) {
    textNode.textContent = tr("identity.devLevel");
    updateDevLevelOptions();
    return;
  }
  field.prepend(document.createTextNode(tr("identity.devLevel")));
  updateDevLevelOptions();
}

function updateDevLevelOptions(): void {
  const select = document.querySelector<HTMLSelectElement>(
    "#player-level-select",
  );
  if (!select) {
    return;
  }
  for (const option of select.options) {
    option.textContent = formatDevLevelOption(
      Number.parseInt(option.value, 10),
    );
  }
}

function formatDevLevelOption(level: number): string {
  const normalizedLevel = normalizeGameplayLevel(level);
  return `${normalizedLevel.toString()} - ${formatRealmLayer(currentLocale, normalizedLevel)}`;
}

function bootstrapGame(): void {
  const scene = new RuntimeScene(
    runtimeStore,
    realtimeClient,
    mobileControls,
    () => currentLocale,
  );
  runtimeScene = scene;
  scene.setTerrainPattern(currentTerrain);
  scene.setSelfSkin(currentSkin);
  gameInstance = new Phaser.Game({
    type: Phaser.AUTO,
    parent: "game",
    backgroundColor: "#05070f",
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    scene: [scene],
  });
  applyAudioMuted();
  window.setTimeout(() => {
    applyAudioMuted();
  }, 0);
  window.addEventListener("resize", () => {
    scheduleViewportSync();
  });
  window.addEventListener("orientationchange", () => {
    scheduleViewportSync();
  });
  window.visualViewport?.addEventListener("resize", () => {
    scheduleViewportSync();
  });
}

function installMobileRuntimeBasics(): void {
  const applySnapshot = () => {
    const snapshot = readMobileRuntimeSnapshot();
    mobileControls.setMobileEnabled(snapshot.isMobile);
    applyMobileRuntimeClasses(document.body, snapshot);
    document.documentElement.style.setProperty(
      "--runtime-viewport-width",
      `${snapshot.viewportWidth.toString()}px`,
    );
    document.documentElement.style.setProperty(
      "--runtime-viewport-height",
      `${snapshot.viewportHeight.toString()}px`,
    );
    gameInstance?.scale.resize(snapshot.viewportWidth, snapshot.viewportHeight);
  };

  applySnapshot();
  window.addEventListener("resize", () => {
    applySnapshot();
    scheduleViewportSync();
  });
  window.addEventListener("orientationchange", () => {
    applySnapshot();
    scheduleViewportSync();
  });
  window.visualViewport?.addEventListener("resize", () => {
    applySnapshot();
    scheduleViewportSync();
  });
  document.addEventListener("contextmenu", (event) => {
    if (document.body.classList.contains("is-mobile-runtime")) {
      event.preventDefault();
    }
  });
}

function installMobileControlUi(): void {
  const root = document.querySelector<HTMLElement>("#mobile-controls");
  const joystick = document.querySelector<HTMLElement>("#mobile-joystick");
  const thumb = document.querySelector<HTMLElement>("#mobile-joystick-thumb");
  const boostButton = document.querySelector<HTMLElement>("#mobile-boost");
  if (!root || !joystick || !thumb || !boostButton) {
    return;
  }
  installMobileControls({
    root,
    joystick,
    thumb,
    boostButton,
    state: mobileControls,
  });
}

function installPwaEnhancements(): void {
  registerServiceWorker();

  const tip = document.querySelector<HTMLElement>("#mobile-pwa-tip");
  const tipText = document.querySelector<HTMLElement>("#mobile-pwa-tip-text");
  const installButton = document.querySelector<HTMLButtonElement>(
    "#mobile-pwa-install",
  );
  const fullscreenButton = document.querySelector<HTMLButtonElement>(
    "#mobile-fullscreen-toggle",
  );
  const dismissButton = document.querySelector<HTMLButtonElement>(
    "#mobile-pwa-dismiss",
  );
  if (
    !tip ||
    !tipText ||
    !installButton ||
    !fullscreenButton ||
    !dismissButton
  ) {
    return;
  }

  let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
  const updateTip = () => {
    const snapshot = readMobileRuntimeSnapshot();
    const standalone = isStandaloneDisplayMode();
    const fullscreenSupported = canUseFullscreen();
    const fullscreenActive = isFullscreenActive();
    const shouldShow =
      snapshot.isMobile && !standalone && !readPwaTipDismissed();
    const copy = getRuntimeEntryCopy(currentLocale, currentIdentityPromptMode, {
      fullscreenActive,
      installPromptAvailable: deferredInstallPrompt !== null,
    });

    tip.classList.toggle("is-hidden", !shouldShow);
    installButton.hidden = deferredInstallPrompt === null;
    fullscreenButton.hidden = !fullscreenSupported;
    installButton.textContent = copy.pwa.install;
    fullscreenButton.textContent = copy.pwa.fullscreen;
    dismissButton.setAttribute("aria-label", copy.pwa.dismissLabel);
    tipText.textContent = copy.pwa.tip;
  };

  for (const element of [tip, installButton, fullscreenButton, dismissButton]) {
    stopGameInputPropagation(element);
  }

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredInstallPrompt = event as BeforeInstallPromptEvent;
    updateTip();
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    persistPwaTipDismissed(true);
    updateTip();
  });
  document.addEventListener("fullscreenchange", () => {
    scheduleViewportSync();
    updateTip();
  });
  document.addEventListener(LOCALE_CHANGED_EVENT, updateTip);
  window.addEventListener("resize", updateTip);
  window.addEventListener("orientationchange", updateTip);

  installButton.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    event.stopPropagation();
    void promptPwaInstall(deferredInstallPrompt).then((promptUsed) => {
      if (promptUsed) {
        deferredInstallPrompt = null;
      }
      updateTip();
    });
  });
  fullscreenButton.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    event.stopPropagation();
    void toggleFullscreen().finally(() => {
      scheduleViewportSync();
      updateTip();
    });
  });
  dismissButton.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    event.stopPropagation();
    persistPwaTipDismissed(true);
    updateTip();
  });

  window.setTimeout(updateTip, 800);
}

function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator) || !import.meta.env.PROD) {
    return;
  }
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      runtimeStore.recordLog(
        formatRuntimeLog(currentLocale, "serviceWorkerFailed"),
        currentLocale,
      );
    });
  });
}

async function promptPwaInstall(
  promptEvent: BeforeInstallPromptEvent | null,
): Promise<boolean> {
  if (!promptEvent) {
    return false;
  }
  await promptEvent.prompt();
  await promptEvent.userChoice.catch(() => null);
  return true;
}

async function toggleFullscreen(): Promise<void> {
  if (isFullscreenActive()) {
    await document.exitFullscreen();
    return;
  }
  await document.documentElement.requestFullscreen();
}

function canUseFullscreen(): boolean {
  return (
    typeof document.documentElement.requestFullscreen === "function" &&
    typeof document.exitFullscreen === "function"
  );
}

function isFullscreenActive(): boolean {
  return document.fullscreenElement !== null;
}

function isStandaloneDisplayMode(): boolean {
  const navigatorWithStandalone = navigator as Navigator & {
    standalone?: boolean;
  };
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    navigatorWithStandalone.standalone === true
  );
}

function readPwaTipDismissed(): boolean {
  try {
    return window.localStorage.getItem(PWA_TIP_DISMISSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistPwaTipDismissed(dismissed: boolean): void {
  try {
    window.localStorage.setItem(
      PWA_TIP_DISMISSED_STORAGE_KEY,
      dismissed ? "1" : "0",
    );
  } catch {
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "pwaStorageUnavailable"),
      currentLocale,
    );
  }
}

async function initializeRuntime(): Promise<void> {
  runtimeStore.patch({
    connectionStatus: "idle",
    statusMessage: formatRuntimeStatus(currentLocale, "entryReady"),
  });

  try {
    const identity = await promptForIdentity(
      runtimeConfig.playerName,
      runtimeConfig.playerLevel,
      "start",
    );
    runtimeConfig.playerName = identity.playerName;
    runtimeConfig.playerLevel = identity.playerLevel;
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "nameConfirmed", {
        name: runtimeConfig.playerName,
      }),
      currentLocale,
    );
    startSpiritRealmMusic();
    realtimeClient.connect(resolveConnectionConfig());
  } catch (error) {
    const detail =
      error instanceof Error ? error.message : "Unknown bootstrap error";
    runtimeStore.patch({
      connectionStatus: "error",
      lastError: detail,
      statusMessage: formatRuntimeStatus(currentLocale, "bootstrapFailed"),
    });
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "startupFailed", { detail }),
      currentLocale,
    );
  }
}

/**
 * Death handling has two flows:
 *  - 小世界 (private) timed-score / team-score rooms: a 3-second on-screen
 *    countdown then auto-respawn in place via {@link RealtimeClient.requestRespawn}
 *    (keeps teamId, so the team-coloured name survives). Any input revives now.
 *  - Everywhere else that allows respawn (the public 灵界 world): the original
 *    death → 身份重弹 reincarnation via {@link restartAfterDeath}.
 * Survival rooms allow neither and keep spectating.
 */
function installDeathRestartPrompt(store: RuntimeStateStore): void {
  const hintEl = document.querySelector<HTMLElement>("#death-spectate-hint");
  let respawnDeadline: number | null = null;
  let respawnTimer: ReturnType<typeof setInterval> | null = null;

  const shouldCountdown = (state: RuntimeViewState): boolean => {
    const self = state.lastSnapshot?.self;
    return Boolean(
      self &&
        !self.isAlive &&
        !identityPromptActive &&
        // The settlement screen takes over once the match is over.
        state.matchSettlement === null &&
        isAutoRespawnRoom(state),
    );
  };

  const stopCountdown = () => {
    const wasActive = respawnTimer !== null || respawnDeadline !== null;
    if (respawnTimer !== null) {
      clearInterval(respawnTimer);
      respawnTimer = null;
    }
    respawnDeadline = null;
    // Only reclaim the hint if we were the ones showing it; survival-mode
    // spectate hints are owned by updateDeathSpectateHint.
    if (wasActive && hintEl) {
      hintEl.hidden = true;
    }
  };

  const renderHint = () => {
    if (respawnDeadline === null || !hintEl) {
      return;
    }
    const seconds = Math.ceil(Math.max(0, respawnDeadline - Date.now()) / 1000);
    hintEl.textContent = tr("room.respawnCountdown", { seconds });
    hintEl.hidden = false;
  };

  const respawnNow = (reason: "auto" | "manual") => {
    const spectate = store.getState().deathSpectate;
    if (spectate) {
      logDeathSpectateEnd(
        reason === "manual" ? "user_input" : "auto_respawn",
        spectate,
      );
    }
    stopCountdown();
    runtimeStore.patch({ deathSpectate: null });
    realtimeClient.requestRespawn();
  };

  const tick = () => {
    if (!shouldCountdown(store.getState())) {
      stopCountdown();
      return;
    }
    if (respawnDeadline !== null && Date.now() >= respawnDeadline) {
      respawnNow("auto");
      return;
    }
    renderHint();
  };

  const startCountdown = () => {
    if (respawnDeadline !== null) {
      return;
    }
    respawnDeadline = Date.now() + RESPAWN_COUNTDOWN_MS;
    renderHint();
    respawnTimer = setInterval(tick, 200);
  };

  // Public-world reincarnation: re-prompt identity then reconnect.
  const requestReincarnation = () => {
    const state = store.getState();
    const self = state.lastSnapshot?.self;
    if (
      !self ||
      self.isAlive ||
      identityPromptActive ||
      deathRestartPromptPending ||
      state.matchSettlement !== null
    ) {
      return;
    }
    promptedDeathPlayerId = state.welcome?.playerId ?? self.id;
    deathRestartPromptPending = true;
    runtimeStore.patch({ deathSpectate: null });
    void restartAfterDeath().finally(() => {
      deathRestartPromptPending = false;
    });
  };

  // One input handler routes to the right flow for the current room.
  const onDeathInput = (event: Event) => {
    if (identityPromptActive) {
      return;
    }
    const state = store.getState();
    if (isAutoRespawnRoom(state)) {
      // Skip the remaining countdown and revive immediately.
      if (respawnDeadline !== null && shouldCountdown(state)) {
        event.preventDefault();
        event.stopPropagation();
        respawnNow("manual");
      }
      return;
    }
    const spectate = state.deathSpectate;
    if (!spectate || !canRespawnInCurrentRoom(state)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    logDeathSpectateEnd("user_input", spectate);
    requestReincarnation();
  };
  document.addEventListener("pointerdown", onDeathInput, true);
  document.addEventListener("keydown", onDeathInput, true);
  document.addEventListener("touchstart", onDeathInput, true);

  store.subscribe((state) => {
    if (shouldCountdown(state)) {
      startCountdown();
      return;
    }
    stopCountdown();

    // Public-world reincarnation flow (respawn allowed but not auto-respawn).
    const self = state.lastSnapshot?.self;
    const playerId = state.welcome?.playerId ?? self?.id ?? null;
    if (
      !self ||
      self.isAlive ||
      identityPromptActive ||
      deathRestartPromptPending
    ) {
      return;
    }
    if (!canRespawnInCurrentRoom(state) || isAutoRespawnRoom(state)) {
      return;
    }
    if (playerId && promptedDeathPlayerId === playerId) {
      return;
    }
    const spectate = state.deathSpectate;
    if (spectate && spectate.selfId === self.id) {
      const target = state.lastSnapshot?.visiblePlayers.find(
        (player) => player.id === spectate.targetId,
      );
      if (target?.isAlive) {
        spectatingDeathPlayerId = playerId;
        return;
      }
      logDeathSpectateEnd(target ? "target_dead" : "target_missing", spectate);
      runtimeStore.patch({ deathSpectate: null });
    }
    if (spectatingDeathPlayerId === playerId) {
      spectatingDeathPlayerId = null;
    }
    requestReincarnation();
  });
}

function logDeathSpectateEnd(
  reason: "target_dead" | "target_missing" | "user_input" | "auto_respawn",
  spectate: NonNullable<RuntimeViewState["deathSpectate"]>,
): void {
  console.info(
    [
      "[game]",
      "death_spectate_end",
      `self=${spectate.selfId}`,
      `target=${spectate.targetId}`,
      `reason=${reason}`,
      `ageMs=${Math.max(0, Date.now() - spectate.startedAt).toString()}`,
    ].join(" "),
  );
}

async function restartAfterDeath(): Promise<void> {
  runtimeStore.patch({
    statusMessage: formatRuntimeStatus(currentLocale, "restartPrompt"),
  });

  try {
    const identity = await promptForIdentity(
      runtimeConfig.playerName,
      runtimeConfig.playerLevel,
      "restart",
    );
    runtimeConfig.playerName = identity.playerName;
    runtimeConfig.playerLevel = identity.playerLevel;
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "restartConfirmed", {
        name: runtimeConfig.playerName,
      }),
      currentLocale,
    );
    promptedDeathPlayerId = null;
    spectatingDeathPlayerId = null;
    startSpiritRealmMusic();
    realtimeClient.restart(resolveConnectionConfig());
  } catch (error) {
    const detail =
      error instanceof Error ? error.message : "Unknown restart error";
    runtimeStore.patch({
      connectionStatus: "error",
      lastError: detail,
      statusMessage: formatRuntimeStatus(currentLocale, "restartFailed"),
    });
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "restartFailed", { detail }),
      currentLocale,
    );
  }
}

function startSpiritRealmMusic(): void {
  runtimeScene?.startSpiritRealmMusic();
}

function restoreMouseLockAfterRespawn(): void {
  runtimeScene?.restoreMouseLockAfterRespawn();
}

function pauseMouseLockForIdentityPrompt(): void {
  runtimeScene?.pauseMouseLockForIdentityPrompt();
}

function installSoundToggle(): void {
  const button = document.querySelector<HTMLButtonElement>("#sound-toggle");
  if (!button) {
    return;
  }

  updateSoundToggle(button);
  button.addEventListener("click", () => {
    audioMuted = !audioMuted;
    persistAudioMuted(audioMuted);
    applyAudioMuted();
    updateSoundToggle(button);
  });
}

function closeRoomPanel(): void {
  document
    .querySelector<HTMLElement>("#room-panel")
    ?.classList.add("is-hidden");
  document
    .querySelector<HTMLButtonElement>("#room-entry-toggle")
    ?.setAttribute("aria-expanded", "false");
}

function installRoomPanel(): void {
  const toggle =
    document.querySelector<HTMLButtonElement>("#room-entry-toggle");
  const panel = document.querySelector<HTMLElement>("#room-panel");
  const close = document.querySelector<HTMLButtonElement>("#room-panel-close");
  if (!toggle || !panel || !close) {
    return;
  }
  stopGameInputPropagation(panel);
  // Upgrade the native config selects to 国潮-styled custom dropdowns, and keep
  // their options mutually valid so illegal combinations can't be picked.
  enhanceRoomSelects(panel);
  installRoomConfigConstraints();
  const setOpen = (open: boolean) => {
    panel.classList.toggle("is-hidden", !open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) {
      updateRoomPanelScale();
    }
  };
  window.addEventListener("resize", updateRoomPanelScale);
  toggle.addEventListener("click", () => {
    setOpen(panel.classList.contains("is-hidden"));
  });
  close.addEventListener("click", () => {
    setOpen(false);
  });
  panel.addEventListener("click", (event) => {
    if (event.target === panel) {
      setOpen(false);
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !panel.classList.contains("is-hidden")) {
      setOpen(false);
    }
  });
  const localize = () => {
    setTextContent("#room-panel-title", tr("settings.room"));
  };
  localize();
  document.addEventListener(LOCALE_CHANGED_EVENT, localize);
}

function installSettingsDialog(): void {
  const toggle = document.querySelector<HTMLButtonElement>("#settings-toggle");
  const dialog = document.querySelector<HTMLElement>("#settings-dialog");
  const close = document.querySelector<HTMLButtonElement>("#settings-close");
  const fullscreenButton = document.querySelector<HTMLButtonElement>(
    "#settings-fullscreen",
  );
  const fullscreenRow = document.querySelector<HTMLElement>(
    "#settings-fullscreen-row",
  );
  if (!toggle || !dialog || !close) {
    return;
  }

  stopGameInputPropagation(dialog);
  stopGameInputPropagation(toggle);

  const setOpen = (open: boolean) => {
    dialog.classList.toggle("is-hidden", !open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) {
      updateSettingsScale();
    }
  };
  window.addEventListener("resize", updateSettingsScale);
  toggle.addEventListener("click", () => {
    setOpen(dialog.classList.contains("is-hidden"));
  });
  close.addEventListener("click", () => {
    setOpen(false);
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) {
      setOpen(false);
    }
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !dialog.classList.contains("is-hidden")) {
      setOpen(false);
    }
  });

  const updateFullscreenLabel = () => {
    if (fullscreenButton) {
      fullscreenButton.textContent = tr(
        isFullscreenActive() ? "pwa.exitFullscreen" : "pwa.fullscreen",
      );
    }
  };
  if (fullscreenRow && !canUseFullscreen()) {
    fullscreenRow.hidden = true;
  } else if (fullscreenButton) {
    updateFullscreenLabel();
    fullscreenButton.addEventListener("click", () => {
      void toggleFullscreen().finally(() => {
        scheduleViewportSync();
        updateFullscreenLabel();
      });
    });
    document.addEventListener("fullscreenchange", updateFullscreenLabel);
  }

  const localizeSettings = () => {
    setTextContent("#settings-title", tr("settings.title"));
    setTextContent("#settings-language-label", tr("settings.language"));
    setTextContent("#settings-sound-label", tr("settings.sound"));
    setTextContent("#settings-fullscreen-label", tr("settings.fullscreen"));
    close.setAttribute("aria-label", tr("settings.close"));
    close.setAttribute("title", tr("settings.close"));
    toggle.setAttribute("aria-label", tr("settings.title"));
    toggle.setAttribute("title", tr("settings.title"));
    setTextContent("[data-settings-caption]", tr("settings.title"));
    updateFullscreenLabel();
  };
  localizeSettings();
  document.addEventListener(LOCALE_CHANGED_EVENT, localizeSettings);
}

function installMouseLockToggle(): void {
  const button =
    document.querySelector<HTMLButtonElement>("#mouse-lock-toggle");
  if (!button) {
    return;
  }

  if (!runtimeScene) {
    return;
  }

  runtimeScene.onMouseLockStatusChange((active) => {
    mouseLockActive = active;
    updateMouseLockToggle(button, active);
  });
  button.addEventListener("click", () => {
    runtimeScene?.toggleMouseLockFromUi();
  });
}

function applyAudioMuted(): void {
  runtimeScene?.setAudioMuted(audioMuted);
}

function updateMouseLockToggle(
  button: HTMLButtonElement,
  active: boolean,
): void {
  button.textContent = active
    ? tr("controls.mouseLockOn")
    : tr("controls.mouseLockOff");
  button.setAttribute("aria-pressed", active ? "true" : "false");
  button.classList.toggle("is-locked", active);
  button.classList.toggle("is-disabled", !active);
  button.title = active
    ? tr("controls.mouseLockTitleOn")
    : tr("controls.mouseLockTitleOff");
}

function updateSoundToggle(button: HTMLButtonElement): void {
  button.textContent = audioMuted
    ? tr("controls.soundOff")
    : tr("controls.soundOn");
  button.setAttribute("aria-pressed", audioMuted ? "true" : "false");
  button.classList.toggle("is-muted", audioMuted);
}

function resolveConnectionConfig(): RealtimeConnectionConfig {
  return {
    wsUrl: runtimeConfig.wsUrl,
    playerName: runtimeConfig.playerName,
    playerKey: readStoredPlayerKey(),
    anonymousPlayerId: readOrCreateAnonymousPlayerId(),
    locale: currentLocale,
    playerLevel: runtimeConfig.playerLevel ?? DEFAULT_PLAYER_LEVEL,
    clientVersion: runtimeConfig.clientVersion,
    reconnectDelayMs: runtimeConfig.reconnectDelayMs,
    skinId: currentSkin,
    joinIntent: pendingJoinIntent,
  };
}

interface WaitingRoomElements {
  overlay: HTMLElement;
  card: HTMLElement | null;
  title: HTMLElement | null;
  inviteLabel: HTMLElement | null;
  code: HTMLElement;
  copy: HTMLButtonElement;
  config: HTMLElement;
  playersTitle: HTMLElement | null;
  players: HTMLUListElement;
  hint: HTMLElement;
  start: HTMLButtonElement;
  leave: HTMLButtonElement;
  resetInvite: HTMLButtonElement | null;
}

function installWaitingRoom(store: RuntimeStateStore): void {
  const els: WaitingRoomElements | null = (() => {
    const overlay = document.querySelector<HTMLElement>("#waiting-room");
    const code = document.querySelector<HTMLElement>("#waiting-invite-code");
    const copy = document.querySelector<HTMLButtonElement>("#waiting-copy");
    const config = document.querySelector<HTMLElement>("#waiting-config");
    const players =
      document.querySelector<HTMLUListElement>("#waiting-players");
    const hint = document.querySelector<HTMLElement>("#waiting-hint");
    const start = document.querySelector<HTMLButtonElement>("#waiting-start");
    const leave = document.querySelector<HTMLButtonElement>("#waiting-leave");
    if (
      !overlay ||
      !code ||
      !copy ||
      !config ||
      !players ||
      !hint ||
      !start ||
      !leave
    ) {
      return null;
    }
    return {
      overlay,
      card: document.querySelector<HTMLElement>(".waiting-card"),
      title: document.querySelector<HTMLElement>("#waiting-title"),
      inviteLabel: document.querySelector<HTMLElement>("#waiting-invite-label"),
      code,
      copy,
      config,
      playersTitle: document.querySelector<HTMLElement>(
        "#waiting-players-title",
      ),
      players,
      hint,
      start,
      leave,
      resetInvite: document.querySelector<HTMLButtonElement>(
        "#waiting-reset-invite",
      ),
    };
  })();

  if (!els) {
    return;
  }

  const copyCurrentInvite = () => {
    const state = store.getState();
    const code = state.waitingRoom?.inviteCode ?? state.roomInfo?.inviteCode;
    if (!code) {
      return;
    }
    const url = buildInviteUrl(code, {
      origin: window.location.origin,
      pathname: window.location.pathname,
    });
    void copyInviteLink(url, els.copy);
  };
  els.copy.addEventListener("click", copyCurrentInvite);
  // The invite code itself is a copy affordance (flashes the copy button).
  els.code.addEventListener("click", copyCurrentInvite);
  els.start.addEventListener("click", () => {
    realtimeClient.startRoom();
  });
  els.leave.addEventListener("click", () => {
    leavePrivateRoom();
  });
  els.resetInvite?.addEventListener("click", () => {
    realtimeClient.resetInvite();
  });

  store.subscribe((state) => {
    renderWaitingRoom(state, els);
  });
  window.addEventListener("resize", updateWaitingRoomScale);
  // A language switch alone doesn't patch the store; force a roster rebuild so
  // localized team names/tags refresh (the cache key also tracks the locale).
  document.addEventListener(LOCALE_CHANGED_EVENT, () => {
    lastWaitingPlayersKey = null;
    renderWaitingRoom(store.getState(), els);
  });
}

function renderWaitingRoom(
  state: RuntimeViewState,
  els: WaitingRoomElements,
): void {
  const room = state.roomInfo;
  const waiting = state.waitingRoom;
  const lobbyState = waiting?.state ?? room?.state ?? "playing";
  const show = room?.kind === "private" && lobbyState === "waiting";
  els.overlay.classList.toggle("is-hidden", !show);
  document.body.classList.toggle("is-waiting-room-active", show);
  if (!show) {
    return;
  }

  const selfId = state.welcome?.playerId ?? null;
  const ownerId = waiting?.ownerId ?? room?.ownerId ?? null;
  const isOwner = selfId !== null && selfId === ownerId;
  const code = waiting?.inviteCode ?? room?.inviteCode ?? "";
  const config = waiting?.config ?? room?.config ?? null;
  const players = waiting?.players ?? [];

  // Solo lobbies use the full-art frame whose height tracks the player count
  // (1 / 2 / 3+); team lobbies keep the plain two-column card.
  const isTeam = config?.matchMode === "team";
  if (els.card) {
    // Both solo and team lobbies use the full-art frame (same 560×787 canvas).
    // is-team-lobby swaps the background + two-column layout; solo uses the
    // fixed 3-row background (list scrolls, so joins don't resize the dialog).
    els.card.classList.add("is-framed");
    els.card.classList.toggle("is-team-lobby", isTeam);
    els.card.classList.toggle("count-3plus", !isTeam);
    els.card.classList.remove("count-1", "count-2");
    updateWaitingRoomScale();
  }

  if (els.title) {
    els.title.textContent = tr("room.waitingTitle");
  }
  if (els.inviteLabel) {
    els.inviteLabel.textContent = tr("room.inviteLabel");
  }
  els.code.textContent = code.length > 0 ? code : "------";
  els.copy.textContent = tr("room.copyInvite");
  els.config.textContent = config ? formatRoomConfigSummary(config) : "";
  if (els.playersTitle) {
    els.playersTitle.textContent = tr("room.playersTitle", {
      count: players.length,
      limit: config?.playerLimit ?? players.length,
    });
  }
  renderWaitingPlayers(
    els.players,
    players,
    ownerId,
    isOwner,
    config?.matchMode === "team",
    selfId,
  );
  els.hint.textContent = isOwner ? tr("room.ownerHint") : tr("room.guestHint");
  if (isOwner) {
    // Survival modes (活到最后) end instantly with a lone player, and team
    // matches need opponents, so the server requires 2+ participants (humans +
    // bots) for both. Mirror that so the owner sees a hint instead of a dead
    // button. Other modes have no minimum.
    const survival =
      config?.winCondition === "last_survivor" ||
      config?.winCondition === "team_elimination";
    const participants = players.length + (config?.botCount ?? 0);
    const canStart = !((survival || isTeam) && participants < 2);
    if (canStart) {
      els.start.textContent = tr("room.startMatch");
      els.start.disabled = false;
      els.start.classList.remove("is-hidden", "is-waiting");
    } else {
      els.start.textContent = tr("room.startNeedsPlayers");
      els.start.disabled = true;
      els.start.classList.remove("is-hidden");
      els.start.classList.add("is-waiting");
    }
  } else {
    // Non-owners (solo or team lobby) see a greyed, disabled "waiting for host"
    // start button rather than an active or missing one.
    els.start.textContent = tr("room.guestHint");
    els.start.disabled = true;
    els.start.classList.remove("is-hidden");
    els.start.classList.add("is-waiting");
  }
  els.leave.textContent = tr("room.leave");
  if (els.resetInvite) {
    els.resetInvite.textContent = tr("room.resetInvite");
    els.resetInvite.classList.toggle("is-hidden", !isOwner);
  }
}

const WAITING_TEAM_IDS = ["azure", "crimson"] as const;

// The waiting room re-renders on every store patch (snapshots, heartbeats).
// Rebuilding the roster DOM under the cursor makes a hovered chip flicker, so
// skip the rebuild unless the roster/ownership/locale actually changed.
let lastWaitingPlayersKey: string | null = null;

function renderWaitingPlayers(
  list: HTMLUListElement,
  players: readonly RoomMember[],
  ownerId: string | null,
  viewerIsOwner: boolean,
  isTeam: boolean,
  selfId: string | null,
): void {
  const key = [
    currentLocale,
    isTeam ? "T" : "S",
    ownerId ?? "",
    viewerIsOwner ? "1" : "0",
    selfId ?? "",
    players
      .map((p) => `${p.id}:${p.name}:${p.teamId ?? ""}:${p.isBot ? "1" : "0"}`)
      .join("|"),
  ].join("~");
  if (key === lastWaitingPlayersKey && list.childElementCount > 0) {
    return;
  }
  lastWaitingPlayersKey = key;

  list.replaceChildren();
  list.classList.toggle("is-teams", isTeam);

  if (isTeam) {
    // Two facing columns (蓝队 / 红队); tap a chip to send it to the other side.
    for (const teamId of WAITING_TEAM_IDS) {
      const members = players.filter(
        (player) => (player.teamId ?? null) === teamId,
      );
      const column = document.createElement("li");
      column.className = `waiting-team-col team-${teamId}`;

      const header = document.createElement("div");
      header.className = "waiting-team-header";
      header.textContent = `${teamDisplayName(teamId)} ${members.length}`;
      column.append(header);

      const columnList = document.createElement("ul");
      columnList.className = "waiting-team-list";
      for (const player of members) {
        columnList.append(
          createTeamChip(player, teamId, ownerId, viewerIsOwner, selfId),
        );
      }
      column.append(columnList);
      list.append(column);
    }
    return;
  }

  for (const player of players) {
    const item = document.createElement("li");
    item.className = "waiting-player";

    // Leftmost slot: the player's chosen cultivator as a full-body portrait.
    const portrait = document.createElement("span");
    portrait.className = "waiting-player-portrait";
    portrait.setAttribute("aria-hidden", "true");
    applySkinPortrait(
      portrait,
      resolveSkinDef(player.skinId, skinSeedFromId(player.id)),
    );
    item.append(portrait);

    const name = document.createElement("span");
    name.className = "waiting-player-name";
    name.textContent = player.name;
    item.append(name);

    if (player.id === ownerId) {
      item.append(createWaitingTag(tr("room.ownerTag"), "is-owner"));
    }
    if (player.isBot) {
      item.append(createWaitingTag(tr("room.botTag"), "is-bot"));
    }
    // Owner can kick any non-owner human.
    if (viewerIsOwner && player.id !== ownerId && !player.isBot) {
      const kick = document.createElement("button");
      kick.type = "button";
      kick.className = "waiting-kick";
      kick.textContent = tr("room.kick");
      const targetId = player.id;
      kick.addEventListener("click", () => {
        realtimeClient.kickPlayer(targetId);
      });
      item.append(kick);
    }
    list.append(item);
  }
}

/**
 * One player chip inside a team column. The name is a button when the viewer
 * may move this player (own chip, or owner moving anyone), and tapping it sends
 * the player to the other side.
 */
function createTeamChip(
  player: RoomMember,
  currentTeam: (typeof WAITING_TEAM_IDS)[number],
  ownerId: string | null,
  viewerIsOwner: boolean,
  selfId: string | null,
): HTMLLIElement {
  const chip = document.createElement("li");
  chip.className = "waiting-team-chip";
  if (player.id === selfId) {
    chip.classList.add("is-self");
  }

  // Left slot: the player's chosen cultivator as a full-body portrait (bots and
  // skinless peers fall back to a stable hash pick).
  const portrait = document.createElement("span");
  portrait.className = "waiting-team-portrait";
  portrait.setAttribute("aria-hidden", "true");
  applySkinPortrait(
    portrait,
    resolveSkinDef(player.skinId, skinSeedFromId(player.id)),
  );
  chip.append(portrait);

  const name = document.createElement("span");
  name.className = "waiting-team-name";
  name.textContent = player.name;
  chip.append(name);

  const canSwap = !player.isBot && (viewerIsOwner || player.id === selfId);
  const otherTeam = currentTeam === "azure" ? "crimson" : "azure";
  if (canSwap) {
    // The whole entry is the swap target: click (or Enter/Space when focused)
    // sends this player to the facing column.
    const targetId = player.id;
    const swap = (): void => {
      realtimeClient.setTeam(targetId, otherTeam);
    };
    chip.classList.add("is-swappable");
    chip.setAttribute("role", "button");
    chip.tabIndex = 0;
    chip.title = tr("room.swapTeam");
    chip.setAttribute("aria-label", `${player.name} · ${tr("room.swapTeam")}`);
    chip.addEventListener("click", swap);
    chip.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        swap();
      }
    });
    // Arrow hints the click moves this player to the facing column: azure sits
    // on the left so it points right (»), crimson on the right points left («).
    // CSS `order` pins it to the seam side of the chip.
    const arrow = document.createElement("span");
    arrow.className = "waiting-team-arrow";
    arrow.setAttribute("aria-hidden", "true");
    arrow.textContent = currentTeam === "azure" ? "»" : "«";
    chip.append(arrow);
  }

  if (player.id === ownerId) {
    chip.append(createWaitingTag(tr("room.ownerTag"), "is-owner"));
  }
  if (player.isBot) {
    chip.append(createWaitingTag(tr("room.botTag"), "is-bot"));
  }
  return chip;
}

function createWaitingTag(label: string, modifier: string): HTMLSpanElement {
  const tag = document.createElement("span");
  tag.className = `waiting-player-tag ${modifier}`;
  tag.textContent = label;
  return tag;
}

function localizeRoomConfigControls(mapSelect: HTMLSelectElement | null): void {
  for (const span of document.querySelectorAll<HTMLElement>(
    "[data-room-config-label]",
  )) {
    const key = span.dataset.roomConfigLabel;
    if (key === "map") span.textContent = tr("room.configMapLabel");
    if (key === "limit") span.textContent = tr("room.configLimitLabel");
    if (key === "bots") span.textContent = tr("room.configBotLabel");
    if (key === "mode") span.textContent = tr("room.configModeLabel");
  }
  if (mapSelect) {
    for (const option of Array.from(mapSelect.options)) {
      const key =
        option.value === "small"
          ? "room.mapSmall"
          : option.value === "medium"
            ? "room.mapMedium"
            : "room.mapLarge";
      option.textContent = tr(key);
    }
  }
  const winSelect = document.querySelector<HTMLSelectElement>(
    "#identity-win-condition",
  );
  if (winSelect) {
    for (const option of Array.from(winSelect.options)) {
      const key =
        option.value === "timed_score"
          ? "room.winTimedScore"
          : option.value === "team_timed_score"
            ? "room.winTeamTimedScore"
            : "room.winLastSurvivor";
      option.textContent = tr(key);
    }
  }
}

function readCreateRoomConfig(
  mapSelect: HTMLSelectElement | null,
  limitSelect: HTMLSelectElement | null,
  botSelect: HTMLSelectElement | null,
  winSelect: HTMLSelectElement | null,
): PrivateRoomConfig {
  const mapSize = (mapSelect?.value ?? "large") as RoomMapSize;
  const playerLimit = Number.parseInt(
    limitSelect?.value ?? "30",
    10,
  ) as PrivateRoomConfig["playerLimit"];
  const botCount = Number.parseInt(
    botSelect?.value ?? "0",
    10,
  ) as PrivateRoomConfig["botCount"];
  const winCondition = (winSelect?.value ??
    "last_survivor") as PrivateRoomConfig["winCondition"];
  const isTeam = winCondition === "team_timed_score";
  const isTimed = winCondition === "timed_score" || isTeam;
  return {
    mapSize,
    playerLimit,
    botCount,
    matchMode: isTeam ? "team" : "solo",
    ...(isTeam ? { teamCount: 2 as const } : {}),
    winCondition,
    ...(isTimed ? { durationSeconds: 600 as const } : {}),
    respawnEnabled: true,
  };
}

/** Rebuilds a numeric select's options, clamping the current value into range. */
function setNumericSelectOptions(
  select: HTMLSelectElement,
  values: readonly number[],
): void {
  const current = Number.parseInt(select.value, 10);
  select.replaceChildren();
  for (const value of values) {
    const option = document.createElement("option");
    option.value = value.toString();
    option.textContent = value.toString();
    select.append(option);
  }
  const valid = values.includes(current)
    ? current
    : (values.filter((v) => v <= current).at(-1) ?? values.at(-1) ?? 0);
  select.value = valid.toString();
}

/** BOT count is any integer 0..(player limit − 2) (leave room for 2 humans). */
function refreshBotOptions(): void {
  const limitSelect = document.querySelector<HTMLSelectElement>(
    "#identity-player-limit",
  );
  const botSelect = document.querySelector<HTMLSelectElement>(
    "#identity-bot-count",
  );
  if (!limitSelect || !botSelect) {
    return;
  }
  const limit = Number.parseInt(limitSelect.value, 10);
  const max = Math.max(0, limit - 2);
  setNumericSelectOptions(
    botSelect,
    Array.from({ length: max + 1 }, (_, i) => i),
  );
}

/** Player-limit options depend on the map's entity cap; then refresh bots. */
function refreshLimitOptions(): void {
  const mapSelect =
    document.querySelector<HTMLSelectElement>("#identity-map-size");
  const limitSelect = document.querySelector<HTMLSelectElement>(
    "#identity-player-limit",
  );
  if (!mapSelect || !limitSelect) {
    return;
  }
  const cap = getRoomMapPreset(mapSelect.value as RoomMapSize).entityCap;
  setNumericSelectOptions(
    limitSelect,
    PLAYER_LIMIT_OPTIONS.filter((v) => v <= cap),
  );
  refreshBotOptions();
}

/**
 * Keep the config dropdowns mutually valid so an illegal combination (and its
 * error) can't be picked: map bounds the player cap, the cap bounds bot count.
 */
function installRoomConfigConstraints(): void {
  const mapSelect =
    document.querySelector<HTMLSelectElement>("#identity-map-size");
  const limitSelect = document.querySelector<HTMLSelectElement>(
    "#identity-player-limit",
  );
  if (!mapSelect || !limitSelect) {
    return;
  }
  mapSelect.addEventListener("change", refreshLimitOptions);
  limitSelect.addEventListener("change", refreshBotOptions);
  refreshLimitOptions();
}

function roomConfigErrorKey(
  reason: PrivateRoomConfigValidationError,
): Parameters<typeof tr>[0] {
  return reason === "player_limit_exceeds_map"
    ? "room.errorMapTooSmall"
    : "room.errorTooManyBots";
}

function formatRoomConfigSummary(config: PrivateRoomConfig): string {
  const mapKey =
    config.mapSize === "small"
      ? "room.mapSmall"
      : config.mapSize === "medium"
        ? "room.mapMedium"
        : "room.mapLarge";
  const modeKey =
    config.matchMode === "team" ? "room.modeTeam" : "room.modeSolo";
  return tr("room.configSummary", {
    mapSize: tr(mapKey),
    limit: config.playerLimit,
    mode: tr(modeKey),
  });
}

async function copyInviteLink(
  url: string,
  button: HTMLButtonElement,
): Promise<void> {
  const restore = () => {
    button.textContent = tr("room.copyInvite");
  };
  try {
    await navigator.clipboard.writeText(url);
    button.textContent = tr("room.copied");
  } catch {
    runtimeStore.recordAudioCue("ui_error");
    button.textContent = tr("room.copyFailed");
  }
  window.setTimeout(restore, 1800);
}

function leavePrivateRoom(): void {
  realtimeClient.disconnect();
  pendingJoinIntent = { kind: "official" };
  const url = new URL(window.location.href);
  url.searchParams.delete("room");
  url.searchParams.delete("createRoom");
  window.location.href = `${url.pathname}${url.search}`;
}

const ROOM_JOIN_ERROR_CODES = new Set([
  "room_not_found",
  "room_closed",
  "room_full",
  "invalid_room_config",
]);

function roomUnavailableMessageKey(code: string): Parameters<typeof tr>[0] {
  if (code === "room_full") return "room.fullMessage";
  if (code === "invalid_room_config") return "room.invalidConfigMessage";
  return "room.endedMessage";
}

/**
 * Shows a clear "room unavailable" screen (instead of a silent retry into an
 * empty room) when a private-room join fails, e.g. the match already ended.
 */
function installRoomUnavailable(store: RuntimeStateStore): void {
  const overlay = document.querySelector<HTMLElement>("#room-unavailable");
  const title = document.querySelector<HTMLElement>("#room-unavailable-title");
  const message = document.querySelector<HTMLElement>(
    "#room-unavailable-message",
  );
  const home = document.querySelector<HTMLButtonElement>(
    "#room-unavailable-home",
  );
  if (!overlay || !title || !message || !home) {
    return;
  }

  home.addEventListener("click", () => {
    leavePrivateRoom();
  });

  store.subscribe((state) => {
    const code = state.lastServerError?.code;
    const show = code !== undefined && ROOM_JOIN_ERROR_CODES.has(code);
    overlay.classList.toggle("is-hidden", !show);
    document.body.classList.toggle("is-room-unavailable", show);
    if (!show || code === undefined) {
      return;
    }
    title.textContent = tr("room.unavailableTitle");
    message.textContent = tr(roomUnavailableMessageKey(code));
    home.textContent = tr("room.backHome");
  });
}

function installMatchHud(store: RuntimeStateStore): void {
  const countdown = document.querySelector<HTMLElement>("#match-countdown");
  const teamHud = document.querySelector<HTMLElement>("#team-hud");
  const overlay = document.querySelector<HTMLElement>("#match-result");
  const title = document.querySelector<HTMLElement>("#match-result-title");
  const winner = document.querySelector<HTMLElement>("#match-result-winner");
  const teams = document.querySelector<HTMLElement>("#match-result-teams");
  const standingsTitle = document.querySelector<HTMLElement>(
    "#match-result-standings-title",
  );
  const standings = document.querySelector<HTMLOListElement>(
    "#match-result-standings",
  );
  const back = document.querySelector<HTMLButtonElement>("#match-result-back");
  if (
    !countdown ||
    !overlay ||
    !title ||
    !winner ||
    !standingsTitle ||
    !standings ||
    !back
  ) {
    return;
  }

  back.addEventListener("click", () => {
    leavePrivateRoom();
  });
  window.addEventListener("resize", updateMatchResultScale);

  store.subscribe((state) => {
    const isPrivate = state.roomInfo?.kind === "private";
    document.body.classList.toggle("is-private-room", isPrivate);
    if (isPrivate) {
      const titleText = document.querySelector<HTMLElement>(
        ".leaderboard-title-text",
      );
      if (titleText) {
        titleText.textContent = tr("room.matchPanelTitle");
      }
    }

    renderMatchCountdown(countdown, state, isPrivate);
    renderTeamHud(teamHud, state, isPrivate);
    renderMatchResult(
      { overlay, title, winner, teams, standingsTitle, standings, back },
      state,
    );
  });
}

function renderTeamHud(
  teamHud: HTMLElement | null,
  state: RuntimeViewState,
  isPrivate: boolean,
): void {
  if (!teamHud) {
    return;
  }
  const teams = state.lastSnapshot?.match?.teams ?? [];
  const show = isPrivate && teams.length >= 2;
  teamHud.classList.toggle("is-hidden", !show);
  if (!show) {
    return;
  }
  teamHud.replaceChildren();
  for (const team of teams) {
    const chip = document.createElement("span");
    chip.className = `team-hud-chip team-${team.teamId}`;
    const name = document.createElement("span");
    name.className = "team-hud-name";
    name.textContent = teamDisplayName(team.teamId);
    const score = document.createElement("span");
    score.className = "team-hud-score";
    score.textContent = formatScore(team.score, currentLocale);
    chip.append(name, score);
    teamHud.append(chip);
  }
}

function renderMatchCountdown(
  countdown: HTMLElement,
  state: RuntimeViewState,
  isPrivate: boolean,
): void {
  const endsAt = state.lastSnapshot?.match?.endsAt ?? null;
  const phase = state.lastSnapshot?.match?.phase ?? "playing";
  const show =
    isPrivate &&
    endsAt !== null &&
    phase === "playing" &&
    state.matchSettlement === null;
  countdown.classList.toggle("is-hidden", !show);
  if (!show || endsAt === null) {
    return;
  }
  const remainingMs = Math.max(0, endsAt - Date.now());
  countdown.textContent = tr("room.countdownLabel", {
    time: formatCountdown(remainingMs),
  });
}

function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString()}:${seconds.toString().padStart(2, "0")}`;
}

interface MatchResultElements {
  overlay: HTMLElement;
  title: HTMLElement;
  winner: HTMLElement;
  teams: HTMLElement | null;
  standingsTitle: HTMLElement;
  standings: HTMLOListElement;
  back: HTMLButtonElement;
}

function setGameKeyboardEnabled(enabled: boolean): void {
  const keyboard = runtimeScene?.input.keyboard;
  if (keyboard) {
    keyboard.enabled = enabled;
  }
}

function installChatPanel(store: RuntimeStateStore): void {
  const panel = document.querySelector<HTMLElement>("#chat-panel");
  const list = document.querySelector<HTMLUListElement>("#chat-messages");
  const form = document.querySelector<HTMLFormElement>("#chat-form");
  const input = document.querySelector<HTMLInputElement>("#chat-input");
  const send = document.querySelector<HTMLButtonElement>("#chat-send");
  if (!panel || !list || !form || !input || !send) {
    return;
  }

  stopGameInputPropagation(panel);
  // Typing must never drive movement/combat: pause Phaser keyboard while
  // the chat input holds focus.
  input.addEventListener("focus", () => {
    setGameKeyboardEnabled(false);
    document.body.classList.add("is-chat-active");
  });
  input.addEventListener("blur", () => {
    setGameKeyboardEnabled(true);
    document.body.classList.remove("is-chat-active");
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    realtimeClient.sendChat(input.value);
    input.value = "";
    // Returning focus to the game re-enables movement keys.
    input.blur();
  });
  // Esc leaves the chat input without sending, handing control back to the game.
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      input.blur();
    }
  });

  // Tab focuses the chat input mid-game so you can keep playing with the mouse
  // and type with the keyboard. Only while chat is available (private room),
  // no dialog is open, and focus is on the game surface — otherwise Tab keeps
  // its normal navigation.
  const overlaySelectors = [
    "#settings-dialog",
    "#room-panel",
    "#appearance-dialog",
    "#waiting-room",
    "#match-result",
    "#identity-gate",
    "#room-unavailable",
  ];
  const isOverlayOpen = (): boolean =>
    overlaySelectors.some((selector) => {
      const el = document.querySelector(selector);
      return el !== null && !el.classList.contains("is-hidden");
    });
  // Capture phase + stopPropagation so we beat the browser's focus navigation
  // (and Phaser's keyboard) to the Tab key — a bubble-phase listener loses that
  // race, so Tab just cycled focus instead of opening chat.
  window.addEventListener(
    "keydown",
    (event) => {
      if (
        event.key !== "Tab" ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return;
      }
      if (
        panel.classList.contains("is-hidden") ||
        document.activeElement === input ||
        isOverlayOpen()
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      input.focus();
    },
    true,
  );

  store.subscribe((state) => {
    const isPrivate = state.roomInfo?.kind === "private";
    panel.classList.toggle("is-hidden", !isPrivate);
    if (!isPrivate) {
      return;
    }
    input.placeholder = tr("room.chatPlaceholder");
    send.textContent = tr("room.chatSend");
    renderChatMessages(list, state);
  });
}

function renderChatMessages(
  list: HTMLUListElement,
  state: RuntimeViewState,
): void {
  const selfId = state.welcome?.playerId ?? null;
  list.replaceChildren();
  for (const message of state.chatMessages) {
    const item = document.createElement("li");
    item.className = "chat-message";
    item.classList.toggle("is-self", message.senderId === selfId);
    const sender = document.createElement("span");
    sender.className = "chat-message-sender";
    sender.textContent = message.senderName;
    const text = document.createElement("span");
    text.className = "chat-message-text";
    text.textContent = message.text;
    item.append(sender, text);
    list.append(item);
  }
  list.scrollTop = list.scrollHeight;
}

/**
 * Default intent for the primary entry button: reincarnating (restart) rejoins
 * the room you are already in (so private-room deaths return to that room, not
 * the official arena); a fresh entry uses the invite-link code or quick play.
 */
function resolveDefaultJoinIntent(
  mode: IdentityPromptMode,
  inviteCodeFromUrl: string | null,
): JoinIntent {
  if (mode === "restart") {
    const room = runtimeStore.getState().roomInfo;
    if (room?.kind === "private" && room.inviteCode) {
      return { kind: "private_join", inviteCode: room.inviteCode };
    }
    return { kind: "official" };
  }
  return inviteCodeFromUrl
    ? { kind: "private_join", inviteCode: inviteCodeFromUrl }
    : { kind: "official" };
}

function teamDisplayName(teamId: string): string {
  if (teamId === "azure") return tr("room.teamAzure");
  if (teamId === "crimson") return tr("room.teamCrimson");
  return teamId;
}

function canRespawnInCurrentRoom(state: RuntimeViewState): boolean {
  const room = state.roomInfo;
  if (!room || room.kind !== "private" || !room.config) {
    return true;
  }
  return isRespawnAllowed(room.config.winCondition, room.config.respawnEnabled);
}

/**
 * 3-second auto-respawn is a 小世界 (private-room) timed-score feature only.
 * The public 灵界 world keeps its death → 身份重弹 reincarnation flow, and
 * survival rooms keep spectating.
 */
function isAutoRespawnRoom(state: RuntimeViewState): boolean {
  const room = state.roomInfo;
  return Boolean(
    room &&
      room.kind === "private" &&
      room.config &&
      isRespawnAllowed(room.config.winCondition, room.config.respawnEnabled),
  );
}

function installSpectatorBar(store: RuntimeStateStore): void {
  const bar = document.querySelector<HTMLElement>("#spectator-bar");
  const label = document.querySelector<HTMLElement>("#spectator-label");
  const prev = document.querySelector<HTMLButtonElement>("#spectator-prev");
  const next = document.querySelector<HTMLButtonElement>("#spectator-next");
  const targets = document.querySelector<HTMLElement>("#spectator-targets");
  if (!bar || !label || !prev || !next || !targets) {
    return;
  }

  const selectableForState = (
    state: RuntimeViewState,
  ): { id: string; name: string }[] => {
    const selfId = state.lastSnapshot?.self.id ?? state.welcome?.playerId ?? "";
    // In team matches, only same-team survivors are selectable (SPEC-1).
    const selfTeamId = state.lastSnapshot?.self.teamId ?? undefined;
    const board = state.lastSnapshot?.leaderboard ?? state.lastLeaderboard;
    return selectableSpectateTargets(
      board.map((entry) => ({
        id: entry.id,
        name: entry.name,
        score: entry.score ?? 0,
        isAlive: entry.isAlive,
        teamId: entry.teamId ?? undefined,
      })),
      selfId,
      selfTeamId,
    ).map((entry) => {
      const found = board.find((item) => item.id === entry.id);
      return { id: entry.id, name: found?.name ?? entry.id };
    });
  };

  const step = (delta: number) => {
    const state = store.getState();
    const options = selectableForState(state);
    if (options.length === 0) {
      return;
    }
    const currentId = state.deathSpectate?.targetId ?? null;
    const currentIndex = options.findIndex((option) => option.id === currentId);
    const nextIndex =
      (((currentIndex === -1 ? 0 : currentIndex + delta) % options.length) +
        options.length) %
      options.length;
    realtimeClient.setSpectateTarget(options[nextIndex]!.id);
  };

  prev.addEventListener("click", () => {
    step(-1);
  });
  next.addEventListener("click", () => {
    step(1);
  });

  store.subscribe((state) => {
    const self = state.lastSnapshot?.self;
    const spectating =
      !!self &&
      !self.isAlive &&
      !canRespawnInCurrentRoom(state) &&
      state.matchSettlement === null;
    bar.classList.toggle("is-hidden", !spectating);
    if (!spectating) {
      return;
    }
    const options = selectableForState(state);
    const currentId = state.deathSpectate?.targetId ?? null;
    const currentName =
      options.find((option) => option.id === currentId)?.name ?? null;
    label.textContent = currentName
      ? tr("room.spectating", { name: currentName })
      : tr("room.spectateNone");
    renderSpectatorTargets(targets, options, currentId);
  });
}

function renderSpectatorTargets(
  container: HTMLElement,
  options: readonly { id: string; name: string }[],
  currentId: string | null,
): void {
  container.replaceChildren();
  for (const option of options) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "spectator-target";
    button.classList.toggle("is-active", option.id === currentId);
    button.textContent = option.name;
    button.addEventListener("click", () => {
      realtimeClient.setSpectateTarget(option.id);
    });
    container.append(button);
  }
}

function renderMatchResult(
  els: MatchResultElements,
  state: RuntimeViewState,
): void {
  const settlement = state.matchSettlement;
  els.overlay.classList.toggle("is-hidden", settlement === null);
  if (!settlement) {
    return;
  }
  updateMatchResultScale();
  els.title.textContent = tr("room.resultTitle");
  els.winner.textContent = settlement.winningTeam
    ? tr("room.teamWinner", { team: teamDisplayName(settlement.winningTeam) })
    : settlement.winnerName
      ? tr("room.winnerLabel", { name: settlement.winnerName })
      : tr("room.noWinner");
  els.winner.className = "match-result-winner";
  if (settlement.winningTeam) {
    els.winner.classList.add(`is-team-${settlement.winningTeam}`);
  }
  // Team score chips (team matches only) so each side's total is visible.
  if (els.teams) {
    els.teams.replaceChildren();
    const sortedTeams = [...settlement.teams].sort(
      (left, right) => right.score - left.score,
    );
    els.teams.classList.toggle("is-hidden", sortedTeams.length === 0);
    for (const team of sortedTeams) {
      const chip = document.createElement("span");
      chip.className = `match-result-team-chip team-${team.teamId}`;
      const label = document.createElement("span");
      label.className = "match-result-team-name";
      label.textContent = teamDisplayName(team.teamId);
      const value = document.createElement("span");
      value.className = "match-result-team-score";
      value.textContent = formatScore(team.score, currentLocale);
      chip.append(label, value);
      els.teams.append(chip);
    }
  }
  els.standingsTitle.textContent = tr("room.standingsTitle");
  els.back.textContent = tr("room.backToEntry");
  els.standings.replaceChildren();
  const sorted = [...settlement.standings].sort(
    (left, right) => right.score - left.score,
  );
  sorted.forEach((standing, index) => {
    const rank = index + 1;
    const item = document.createElement("li");
    item.className = "match-result-row";
    if (rank === 1) {
      item.classList.add("is-champion");
    }
    // Colour the row by team so友军/敌军 read at a glance in team matches.
    if (standing.teamId) {
      item.classList.add(`is-team-${standing.teamId}`);
    }
    // Rank medallion: gold / azure / jade tiers echo the mockup.
    const badge = document.createElement("span");
    badge.className = `match-result-rank rank-${Math.min(rank, 5).toString()}`;
    badge.textContent = rank.toString();
    const name = document.createElement("span");
    name.className = "match-result-row-name";
    name.textContent = standing.name;
    const score = document.createElement("span");
    score.className = "match-result-row-score";
    score.textContent = formatScore(standing.score, currentLocale);
    item.append(badge, name, score);
    els.standings.append(item);
  });
}

function sendViewportToRealtime(): void {
  const viewport = readCurrentViewport();
  realtimeClient.sendViewport(viewport.width, viewport.height, viewport.zoom);
}

function installViewportSync(store: RuntimeStateStore): void {
  let lastWelcomePlayerId: string | null = null;
  store.subscribe((state) => {
    const playerId = state.welcome?.playerId ?? null;
    if (!playerId || playerId === lastWelcomePlayerId) {
      return;
    }
    lastWelcomePlayerId = playerId;
    scheduleViewportSync();
  });
}

function scheduleViewportSync(): void {
  for (const delayMs of [0, 120, 360]) {
    window.setTimeout(() => {
      sendViewportToRealtime();
    }, delayMs);
  }
}

function readCurrentViewport(): {
  width: number;
  height: number;
  zoom: number;
} {
  const scale = gameInstance?.scale;
  const mobileSnapshot = readMobileRuntimeSnapshot();
  const width = mobileSnapshot.isMobile
    ? mobileSnapshot.viewportWidth
    : (scale?.width ?? window.innerWidth);
  const height = mobileSnapshot.isMobile
    ? mobileSnapshot.viewportHeight
    : (scale?.height ?? window.innerHeight);
  const zoom = gameInstance?.scene.getScenes(true)[0]?.cameras.main.zoom ?? 1;
  return {
    width: Math.max(1, width),
    height: Math.max(1, height),
    zoom: Math.max(0.1, zoom),
  };
}

interface PlayerIdentity {
  playerName: string;
  playerLevel: number;
}

async function promptForIdentity(
  currentName: string,
  currentLevel: number | null,
  mode: IdentityPromptMode,
): Promise<PlayerIdentity> {
  if (mode === "restart") {
    pauseMouseLockForIdentityPrompt();
  }
  identityPromptActive = true;
  const defaultName =
    normalizePlayerName(currentName) ?? readStoredPlayerName() ?? "";
  const defaultLevel = DEV_LEVEL_SELECTOR_ENABLED
    ? normalizeGameplayLevel(
        currentLevel ?? readStoredPlayerLevel() ?? DEFAULT_PLAYER_LEVEL,
      )
    : DEFAULT_PLAYER_LEVEL;

  try {
    return await requestIdentity(defaultName, defaultLevel, mode);
  } finally {
    identityPromptActive = false;
  }
}

function requestIdentity(
  initialName: string,
  initialLevel: number,
  mode: IdentityPromptMode,
): Promise<PlayerIdentity> {
  const gate = document.querySelector<HTMLElement>("#identity-gate");
  const form = document.querySelector<HTMLFormElement>("#identity-form");
  const input = document.querySelector<HTMLInputElement>("#player-name-input");
  const error = document.querySelector<HTMLElement>("#player-name-error");
  const submit = document.querySelector<HTMLButtonElement>("#identity-submit");

  if (!gate || !form || !input || !error || !submit) {
    throw new Error("Nickname dialog is not available.");
  }

  currentIdentityPromptMode = mode;
  applyRuntimeEntryLocale();
  const levelSelect = ensureDevLevelSelect(form);
  ensureAppearanceUi(form);
  syncAppearanceUi();
  document.dispatchEvent(new Event(CLOSE_MOBILE_LEADERBOARD_EVENT));
  gate.classList.remove("is-hidden");
  document.body.classList.add("is-identity-prompt-active");
  // Give first-timers a ready-made name instead of an empty field + hint, so
  // they can dive straight in (or type over it).
  input.value = initialName || pickRandomPlayerName();
  if (levelSelect) {
    levelSelect.value = normalizeGameplayLevel(initialLevel).toString();
  }
  error.textContent = "";
  submit.textContent = tr(
    mode === "restart" ? "identity.restart" : "identity.start",
  );
  refreshHudForIdentityPrompt();

  window.setTimeout(() => {
    input.focus({ preventScroll: true });
    if (!readMobileRuntimeSnapshot().isMobile) {
      input.select();
      return;
    }
    input.setSelectionRange(input.value.length, input.value.length);
  }, 0);

  const createButton = document.querySelector<HTMLElement>(
    "#identity-create-room",
  );
  const joinButton = document.querySelector<HTMLButtonElement>(
    "#identity-join-room",
  );
  const inviteInput = document.querySelector<HTMLInputElement>(
    "#identity-invite-input",
  );
  const roomError = document.querySelector<HTMLElement>("#identity-room-error");
  // Create-config errors sit under the map/players/bot row; the shared
  // #identity-room-error is reserved for invite/join errors near the input.
  const createError = document.querySelector<HTMLElement>(
    "#identity-create-error",
  );
  const roomEntryToggle =
    document.querySelector<HTMLButtonElement>("#room-entry-toggle");
  const mapSelect =
    document.querySelector<HTMLSelectElement>("#identity-map-size");
  const limitSelect = document.querySelector<HTMLSelectElement>(
    "#identity-player-limit",
  );
  const botSelect = document.querySelector<HTMLSelectElement>(
    "#identity-bot-count",
  );
  const winSelect = document.querySelector<HTMLSelectElement>(
    "#identity-win-condition",
  );
  const confirmButton = document.querySelector<HTMLButtonElement>(
    "#identity-create-confirm",
  );

  // The room icon + create/join panel are available on both a fresh entry and a
  // death restart (转世重生 also lets you switch worlds).
  const roomModeEnabled = true;
  const isFreshStart = mode === "start";
  // Arriving via an invite link (?room=CODE) — only on a fresh start: make
  // joining that room the primary action and hide the create/join clutter.
  const inviteCodeFromUrl =
    isFreshStart && pendingJoinIntent.kind === "private_join"
      ? pendingJoinIntent.inviteCode
      : null;
  // The room icon is hidden only when arriving via an invite link.
  roomEntryToggle?.classList.toggle("is-hidden", inviteCodeFromUrl !== null);
  closeRoomPanel();
  if (roomModeEnabled && inviteCodeFromUrl === null) {
    if (createButton) createButton.textContent = tr("room.createRoom");
    if (confirmButton) confirmButton.textContent = tr("room.createConfirm");
    if (joinButton) joinButton.textContent = tr("room.joinRoom");
    setTextContent("#identity-join-title", tr("room.joinSectionTitle"));
    setTextContent("#identity-or-label", tr("room.orLabel"));
    if (inviteInput) {
      inviteInput.placeholder = tr("room.invitePlaceholder");
      inviteInput.value = "";
    }
    localizeRoomConfigControls(mapSelect);
    if (roomError) roomError.textContent = "";
    if (createError) createError.textContent = "";
  }
  if (inviteCodeFromUrl !== null) {
    submit.textContent = tr("room.joinPrimary", { code: inviteCodeFromUrl });
  }

  return new Promise((resolve) => {
    const finish = (intent: JoinIntent, normalizedName: string): void => {
      const selectedLevel = levelSelect
        ? normalizeGameplayLevel(Number.parseInt(levelSelect.value, 10))
        : DEFAULT_PLAYER_LEVEL;

      pendingJoinIntent = intent;
      analytics.track("landing_play_click", {
        mode,
        requestedLevel: selectedLevel,
        mobile: readMobileRuntimeSnapshot().isMobile,
        standalone: isStandaloneDisplayMode(),
      });
      persistIdentity(normalizedName, selectedLevel);
      runtimeStore.recordAudioCue(
        mode === "restart" ? "ui_reincarnate" : "ui_start",
      );
      if (mode === "restart") {
        restoreMouseLockAfterRespawn();
      } else {
        document.body.classList.add("is-gameplay-active");
      }
      gate.classList.add("is-hidden");
      closeRoomPanel();
      document.body.classList.remove("is-identity-prompt-active");
      document.dispatchEvent(new Event(CLOSE_MOBILE_LEADERBOARD_EVENT));
      refreshHudForIdentityPrompt();
      error.textContent = "";
      if (roomError) roomError.textContent = "";
      input.removeEventListener("focus", handleFocus);
      form.removeEventListener("submit", handleSubmit);
      confirmButton?.removeEventListener("click", handleCreate);
      joinButton?.removeEventListener("click", handleJoin);
      resolve({ playerName: normalizedName, playerLevel: selectedLevel });
    };

    const requireName = (): string | null => {
      // First-timers can just start: an empty name gets a random one filled in.
      if (input.value.trim().length === 0) {
        const randomName = pickRandomPlayerName();
        input.value = randomName;
        error.textContent = "";
        return randomName;
      }
      const normalizedName = normalizePlayerName(input.value);
      if (!normalizedName) {
        runtimeStore.recordAudioCue("ui_error");
        error.textContent = tr("identity.nameLengthError", {
          max: PLAYER_NAME_MAX_LENGTH,
        });
        input.focus();
        return null;
      }
      return normalizedName;
    };

    const handleSubmit = (event: SubmitEvent) => {
      event.preventDefault();
      const normalizedName = requireName();
      if (!normalizedName) {
        return;
      }
      finish(resolveDefaultJoinIntent(mode, inviteCodeFromUrl), normalizedName);
    };

    const handleCreate = () => {
      const config = readCreateRoomConfig(
        mapSelect,
        limitSelect,
        botSelect,
        winSelect,
      );
      const validation = validatePrivateRoomConfig(config);
      if (!validation.ok) {
        runtimeStore.recordAudioCue("ui_error");
        if (createError) {
          createError.textContent = tr(roomConfigErrorKey(validation.reason));
        }
        return;
      }
      if (createError) {
        createError.textContent = "";
      }
      const normalizedName = requireName();
      if (normalizedName) {
        finish({ kind: "private_create", config }, normalizedName);
      }
    };

    const handleJoin = () => {
      const code = normalizeInviteCodeInput(inviteInput?.value ?? "");
      if (!isValidInviteCode(code)) {
        runtimeStore.recordAudioCue("ui_error");
        if (roomError) roomError.textContent = tr("room.inviteError");
        inviteInput?.focus();
        return;
      }
      const normalizedName = requireName();
      if (normalizedName) {
        finish({ kind: "private_join", inviteCode: code }, normalizedName);
      }
    };

    input.addEventListener("focus", handleFocus);
    form.addEventListener("submit", handleSubmit);
    if (roomModeEnabled) {
      confirmButton?.addEventListener("click", handleCreate);
      joinButton?.addEventListener("click", handleJoin);
    }
  });
}

function refreshHudForIdentityPrompt(): void {
  runtimeStore.patch({
    statusMessage: runtimeStore.getState().statusMessage,
  });
}

function ensureDevLevelSelect(
  form: HTMLFormElement | null,
): HTMLSelectElement | null {
  if (!DEV_LEVEL_SELECTOR_ENABLED || !form) {
    return null;
  }

  const existing = form.querySelector<HTMLSelectElement>(
    "#player-level-select",
  );
  if (existing) {
    return existing;
  }

  const field = document.createElement("label");
  field.className = "identity-dev-level";
  field.htmlFor = "player-level-select";
  field.textContent = tr("identity.devLevel");

  const select = document.createElement("select");
  select.id = "player-level-select";
  select.name = "playerLevel";
  select.className = "identity-level-select";

  for (
    let level = DEFAULT_PLAYER_LEVEL;
    level <= MAX_GAMEPLAY_LEVEL;
    level += 1
  ) {
    const option = document.createElement("option");
    option.value = level.toString();
    option.textContent = formatDevLevelOption(level);
    select.append(option);
  }

  field.append(select);
  form.insertBefore(field, form.querySelector(".identity-error"));
  return select;
}

function readStoredPlayerLevel(): number | null {
  if (!DEV_LEVEL_SELECTOR_ENABLED) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(PLAYER_LEVEL_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return normalizeGameplayLevel(Number.parseInt(raw, 10));
  } catch {
    return null;
  }
}

function handleFocus(): void {
  runtimeStore.recordAudioCue("ui_focus", { volumeScale: 0.75 });
}

function normalizePlayerName(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed.length < 1 || trimmed.length > PLAYER_NAME_MAX_LENGTH) {
    return null;
  }

  return trimmed;
}

/** Pick a locale-appropriate common name for players who start without one. */
function pickRandomPlayerName(): string {
  const fallback = RANDOM_PLAYER_NAMES["zh-CN"] ?? [];
  const pool = RANDOM_PLAYER_NAMES[currentLocale] ?? fallback;
  if (pool.length === 0) {
    return "无名修士";
  }
  const index = Math.floor(Math.random() * pool.length);
  return pool[index] ?? "无名修士";
}

function readStoredPlayerName(): string | null {
  try {
    return normalizePlayerName(
      window.localStorage.getItem(PLAYER_NAME_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

function readStoredPlayerKey(): string | null {
  try {
    const value = window.localStorage.getItem(PLAYER_KEY_STORAGE_KEY);
    return normalizePlayerKey(value);
  } catch {
    return null;
  }
}

function readOrCreateAnonymousPlayerId(): string | null {
  try {
    const stored = window.localStorage.getItem(ANONYMOUS_PLAYER_ID_STORAGE_KEY);
    if (stored && stored.length >= 8 && stored.length <= 80) {
      return stored;
    }
    const generated = crypto.randomUUID();
    window.localStorage.setItem(ANONYMOUS_PLAYER_ID_STORAGE_KEY, generated);
    return generated;
  } catch {
    return null;
  }
}

function persistPlayerKey(playerKey: string): void {
  const normalized = normalizePlayerKey(playerKey);
  if (!normalized) {
    return;
  }
  try {
    window.localStorage.setItem(PLAYER_KEY_STORAGE_KEY, normalized);
  } catch {
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "playerKeyStorageUnavailable"),
      currentLocale,
    );
  }
}

function normalizePlayerKey(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length >= 8 && trimmed.length <= 80 ? trimmed : null;
}

function persistIdentity(playerName: string, playerLevel: number): void {
  try {
    window.localStorage.setItem(PLAYER_NAME_STORAGE_KEY, playerName);
    if (DEV_LEVEL_SELECTOR_ENABLED) {
      window.localStorage.setItem(
        PLAYER_LEVEL_STORAGE_KEY,
        normalizeGameplayLevel(playerLevel).toString(),
      );
    }
  } catch {
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "identityStorageUnavailable"),
      currentLocale,
    );
  }
}

function readStoredAudioMuted(): boolean {
  try {
    return window.localStorage.getItem(AUDIO_MUTED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function persistAudioMuted(muted: boolean): void {
  try {
    window.localStorage.setItem(AUDIO_MUTED_STORAGE_KEY, muted ? "1" : "0");
  } catch {
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "audioStorageUnavailable"),
      currentLocale,
    );
  }
}

function installTreasureHudAndNotifications(store: RuntimeStateStore): void {
  const shell = document.querySelector<HTMLElement>("#game-shell");
  if (!shell) {
    return;
  }

  const hud = document.createElement("div");
  hud.className = "treasure-self-hud is-hidden";
  hud.setAttribute("aria-live", "polite");
  shell.append(hud);

  const stack = document.createElement("div");
  stack.className = "treasure-notification-stack";
  stack.setAttribute("aria-live", "polite");
  shell.append(stack);

  let lastAnnouncedTreasureId: string | null = null;
  let lastPickedTreasureId: string | null = null;
  let lastTreasureGuardAt: number | null = null;
  let lastSelfTreasureKinds: TreasureKind[] = [];
  let spawnNotice: TreasureNotice | null = null;
  const toasts: TreasureNotice[] = [];

  store.subscribe((state) => {
    const snapshot = state.lastSnapshot;
    updateSelfTreasureHud(hud, state);
    const selfTreasureKinds = getSelfTreasureKinds(state);

    const unclaimed = snapshot?.unclaimedTreasure ?? null;
    const announcement = state.lastTreasureAnnouncement;
    const pickedTreasure = state.lastTreasurePickup;
    const newlyHeldTreasure = findNewlyHeldTreasure(
      lastSelfTreasureKinds,
      selfTreasureKinds,
    );
    const shouldPlayTreasureAcquired = newlyHeldTreasure !== null;
    if (shouldPlayTreasureAcquired && spawnNotice?.persistent) {
      lastPickedTreasureId = spawnNotice.sourceId;
      spawnNotice.expiresAt = Date.now();
      spawnNotice.persistent = false;
    } else if (
      pickedTreasure &&
      pickedTreasure.id !== lastPickedTreasureId &&
      pickedTreasure.id === spawnNotice?.sourceId
    ) {
      spawnNotice.expiresAt = Date.now();
      spawnNotice.persistent = false;
      lastPickedTreasureId = pickedTreasure.id;
    } else if (
      announcement &&
      announcement.id !== lastAnnouncedTreasureId &&
      announcement.id !== spawnNotice?.sourceId
    ) {
      spawnNotice = createTreasureNotice({
        id: `spawn:${announcement.id}`,
        sourceId: announcement.id,
        kind: announcement.kind,
        title: formatTreasureSpawnTitle(currentLocale),
        body: getTreasureUiConfig(announcement.kind, currentLocale).shortEffect,
        persistent: true,
      });
      lastAnnouncedTreasureId = announcement.id;
    } else if (
      unclaimed &&
      unclaimed.id !== spawnNotice?.sourceId &&
      unclaimed.id !== lastPickedTreasureId
    ) {
      spawnNotice = createTreasureNotice({
        id: `spawn:${unclaimed.id}`,
        sourceId: unclaimed.id,
        kind: unclaimed.kind,
        title: formatTreasureSpawnTitle(currentLocale),
        body: getTreasureUiConfig(unclaimed.kind, currentLocale).shortEffect,
        persistent: true,
      });
    } else if (
      unclaimed &&
      unclaimed.id === spawnNotice?.sourceId &&
      unclaimed.id !== lastPickedTreasureId &&
      !spawnNotice.persistent
    ) {
      spawnNotice.persistent = true;
      spawnNotice.expiresAt = Date.now() + 4200;
    }

    if (newlyHeldTreasure) {
      const config = getTreasureUiConfig(newlyHeldTreasure, currentLocale);
      toasts.push(
        createTreasureNotice({
          id: `pickup:${newlyHeldTreasure}:${Date.now().toString()}`,
          kind: newlyHeldTreasure,
          title: formatTreasurePickupTitle(currentLocale),
          body: config.shortEffect,
          persistent: false,
        }),
      );
    }
    lastSelfTreasureKinds = [...selfTreasureKinds];
    if (shouldPlayTreasureAcquired) {
      store.recordAudioCue("treasure_acquired", { volumeScale: 0.86 });
    }

    const guard = state.lastTreasureGuard;
    if (guard && guard.createdAt !== lastTreasureGuardAt) {
      const isSelfGuard = guard.playerId === snapshot?.self.id;
      toasts.push(
        createTreasureNotice({
          id: `guard:${guard.playerId}:${guard.createdAt.toString()}`,
          sourceId: `guard:${guard.playerId}`,
          kind: "black_tortoise_armor",
          title: formatTreasureGuardTitle(currentLocale),
          body: formatTreasureGuardBody(
            currentLocale,
            guard.remainingCharges,
            isSelfGuard,
          ),
          persistent: false,
        }),
      );
      lastTreasureGuardAt = guard.createdAt;
    }

    const ritual = state.ascensionRitual;
    if (ritual && ritual.phase === "ascending" && !ritual.isSelf) {
      const id = `ascension:${ritual.ascenderId}:${ritual.endsAt.toString()}`;
      if (!toasts.some((notice) => notice.id === id)) {
        const notice = formatAscensionNotice(
          currentLocale,
          ritual.ascenderName,
        );
        toasts.push({
          id,
          sourceId: id,
          title: notice.title,
          treasureName: null,
          body: notice.body,
          iconPath: "",
          glow: "rgba(201, 160, 87, 0.4)",
          createdAt: Date.now(),
          expiresAt: Date.now() + 5200,
          persistent: false,
        });
      }
    }

    renderTreasureNotifications(stack, spawnNotice, toasts);
  });
}

function installDebugHandle(): void {
  window.__XIUXIAN_RUNTIME__ = {
    config: runtimeConfig,
    getState: () => runtimeStore.getState(),
    requestRespawn: () => realtimeClient.requestRespawn(),
    sendInput: (angle: number, boost = false) =>
      realtimeClient.sendInput(angle, boost),
    sendViewport: () => sendViewportToRealtime(),
  };
}

declare global {
  interface Window {
    __XIUXIAN_RUNTIME__?: {
      config: typeof runtimeConfig;
      getState: () => RuntimeViewState;
      requestRespawn: () => void;
      sendInput: (angle: number, boost?: boolean) => void;
      sendViewport: () => void;
    };
  }
}

function installHud(store: RuntimeStateStore): void {
  const leaderboard = document.querySelector<HTMLElement>("#leaderboard-list");
  const leaderboardPanel = document.querySelector<HTMLElement>(
    ".hud-panel-leaderboard",
  );
  const collapsedSelf = document.querySelector<HTMLElement>(
    "#leaderboard-collapsed-self",
  );
  const collapsedLevel = document.querySelector<HTMLElement>(
    "#leaderboard-collapsed-level",
  );
  const collapsedScore = document.querySelector<HTMLElement>(
    "#leaderboard-collapsed-score",
  );
  const collapsedSpectators = document.querySelector<HTMLElement>(
    "#leaderboard-collapsed-spectators",
  );
  const mobileLeaderboardToggle = document.querySelector<HTMLButtonElement>(
    "#mobile-leaderboard-toggle",
  );
  const deathSpectateHint = document.querySelector<HTMLElement>(
    "#death-spectate-hint",
  );
  let mobileLeaderboardOpen = false;
  let lastLeaderboardHtml: string | null = null;
  let hintedDeathSpectateKey: string | null = null;
  let deathSpectateHintTimeout: ReturnType<typeof setTimeout> | null = null;
  const setMobileLeaderboardOpen = (open: boolean) => {
    mobileLeaderboardOpen = open;
    document.body.classList.toggle("is-mobile-leaderboard-open", open);
    mobileLeaderboardToggle?.setAttribute("aria-expanded", open.toString());
  };

  document.addEventListener(CLOSE_MOBILE_LEADERBOARD_EVENT, () => {
    setMobileLeaderboardOpen(false);
  });
  document.addEventListener(LOCALE_CHANGED_EVENT, () => {
    updateCollapsedLeaderboardSelf(
      collapsedSelf,
      collapsedLevel,
      collapsedScore,
      collapsedSpectators,
      store.getState(),
    );
  });

  if (mobileLeaderboardToggle) {
    stopGameInputPropagation(mobileLeaderboardToggle);
    mobileLeaderboardToggle.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      event.stopPropagation();
      setMobileLeaderboardOpen(!mobileLeaderboardOpen);
    });
  }
  if (leaderboardPanel) {
    stopGameInputPropagation(leaderboardPanel);
  }

  const leaderboardCollapse = document.querySelector<HTMLButtonElement>(
    "#leaderboard-collapse",
  );
  const setLeaderboardCollapsed = (collapsed: boolean) => {
    document.body.classList.toggle("is-leaderboard-collapsed", collapsed);
    leaderboardCollapse?.setAttribute("aria-expanded", (!collapsed).toString());
    writeStoredLeaderboardCollapsed(collapsed);
  };
  setLeaderboardCollapsed(readStoredLeaderboardCollapsed());
  if (leaderboardCollapse) {
    stopGameInputPropagation(leaderboardCollapse);
    leaderboardCollapse.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      setLeaderboardCollapsed(
        !document.body.classList.contains("is-leaderboard-collapsed"),
      );
    });
  }

  store.subscribe((state) => {
    if (state.welcome?.playerKey) {
      persistPlayerKey(state.welcome.playerKey);
    }
    if (leaderboard) {
      updateLeaderboardPanel(
        leaderboard,
        leaderboardPanel,
        createLeaderboardRenderState(state),
        (html) => {
          if (html === lastLeaderboardHtml) {
            return false;
          }
          lastLeaderboardHtml = html;
          return true;
        },
      );
    }
    updateCollapsedLeaderboardSelf(
      collapsedSelf,
      collapsedLevel,
      collapsedScore,
      collapsedSpectators,
      state,
    );
    const hintState = updateDeathSpectateHint(
      deathSpectateHint,
      state,
      hintedDeathSpectateKey,
      deathSpectateHintTimeout,
    );
    hintedDeathSpectateKey = hintState.hintedKey;
    deathSpectateHintTimeout = hintState.timeout;
    document.body.classList.toggle(
      "is-death-spectating",
      Boolean(state.deathSpectate && !identityPromptActive),
    );
  });

  document.addEventListener(
    "pointerdown",
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const button = target.closest<HTMLButtonElement>("[data-period]");
      const period = button?.dataset.period;
      if (!button || !isPeriodLeaderboardKind(period)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      selectedPeriod = period;
      persistLeaderboardPeriod(period);
      const state = createLeaderboardRenderState(store.getState());
      if (leaderboard) {
        updateLeaderboardPanel(leaderboard, leaderboardPanel, state, (html) => {
          lastLeaderboardHtml = html;
          return true;
        });
      }
    },
    true,
  );
}

function updateDeathSpectateHint(
  element: HTMLElement | null,
  state: RuntimeViewState,
  hintedKey: string | null,
  timeout: ReturnType<typeof setTimeout> | null,
): {
  hintedKey: string | null;
  timeout: ReturnType<typeof setTimeout> | null;
} {
  if (!element) {
    return { hintedKey, timeout };
  }
  if (!state.deathSpectate || identityPromptActive) {
    element.hidden = true;
    if (timeout) {
      clearTimeout(timeout);
    }
    return { hintedKey: null, timeout: null };
  }
  // Private timed-score rooms: the auto-respawn countdown owns the hint element.
  if (isAutoRespawnRoom(state)) {
    if (timeout) {
      clearTimeout(timeout);
    }
    return { hintedKey: null, timeout: null };
  }
  const nextHintedKey = `${state.deathSpectate.selfId}:${state.deathSpectate.startedAt.toString()}`;
  if (hintedKey === nextHintedKey) {
    return { hintedKey, timeout };
  }
  if (timeout) {
    clearTimeout(timeout);
  }
  element.textContent = formatRuntimeStatus(currentLocale, "spectatingKiller");
  element.hidden = false;
  const nextTimeout = setTimeout(() => {
    element.hidden = true;
  }, DEATH_SPECTATE_HINT_VISIBLE_MS);
  return { hintedKey: nextHintedKey, timeout: nextTimeout };
}

interface TreasureNotice {
  id: string;
  sourceId: string;
  title: string;
  treasureName: string | null;
  body: string;
  iconPath: string;
  glow: string;
  createdAt: number;
  expiresAt: number;
  persistent: boolean;
}

/**
 * Per-treasure halo colour for the large notice / self-HUD icons. Q 版 treasure
 * art is a clean cutout (no baked glow), so the "灵光" comes from the render
 * layer here, tinted by each treasure's own {@link TreasureUiConfig.glowColor}.
 */
function treasureGlowColor(color: number, alpha = 0.5): string {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  return `rgba(${r.toString()}, ${g.toString()}, ${b.toString()}, ${alpha.toString()})`;
}

function createTreasureNotice(options: {
  id: string;
  sourceId?: string;
  kind: TreasureKind;
  title: string;
  body: string;
  persistent: boolean;
}): TreasureNotice {
  const config = getTreasureUiConfig(options.kind, currentLocale);
  return {
    id: options.id,
    sourceId: options.sourceId ?? options.id,
    title: options.title,
    treasureName: config.name,
    body: options.body,
    iconPath: config.imagePath,
    glow: treasureGlowColor(config.glowColor),
    createdAt: Date.now(),
    expiresAt: Date.now() + 4200,
    persistent: options.persistent,
  };
}

function renderSelfTreasureHud(
  hud: HTMLElement,
  kinds: readonly TreasureKind[],
  slotCount: number,
): void {
  const slots = createTreasureSlotViews(kinds, slotCount, currentLocale);
  if (slots.every((slot) => slot.kind === null)) {
    hud.className = "treasure-self-hud is-hidden";
    hud.innerHTML = "";
    return;
  }

  const filledSlots = slots.filter((slot) => slot.kind !== null);
  const activeNames = filledSlots.map((slot) => slot.label).join(" / ");
  hud.className = "treasure-self-hud";
  hud.innerHTML = `
    <span class="treasure-self-slots">
      ${slots
        .map((slot) =>
          slot.kind && slot.imagePath
            ? `<span class="treasure-self-slot is-filled"><img class="treasure-self-icon" src="${slot.imagePath}" alt="${escapeHtml(slot.label)}" style="--treasure-glow:${treasureGlowColor(getTreasureUiConfig(slot.kind, currentLocale).glowColor)}"></span>`
            : `<span class="treasure-self-slot is-empty" aria-label="${escapeHtml(slot.label)}"></span>`,
        )
        .join("")}
    </span>
    <span class="treasure-self-copy">
      <strong>${escapeHtml(activeNames)}</strong>
      <small>${filledSlots.length.toString()}/${slots.length.toString()}</small>
    </span>
  `;
}

function updateSelfTreasureHud(
  hud: HTMLElement,
  state: RuntimeViewState,
): void {
  const selfTreasureKinds = getSelfTreasureKinds(state);
  const selfTreasureSlotCount =
    state.lastSnapshot?.self.treasureSlotCount ??
    Math.max(1, selfTreasureKinds.length);
  renderSelfTreasureHud(hud, selfTreasureKinds, selfTreasureSlotCount);
}

function getSelfTreasureKinds(state: RuntimeViewState): TreasureKind[] {
  const self = state.lastSnapshot?.self;
  return self?.treasureKinds ?? (self?.treasureKind ? [self.treasureKind] : []);
}

function findNewlyHeldTreasure(
  previousKinds: readonly TreasureKind[],
  currentKinds: readonly TreasureKind[],
): TreasureKind | null {
  const previousCounts = new Map<TreasureKind, number>();
  for (const kind of previousKinds) {
    previousCounts.set(kind, (previousCounts.get(kind) ?? 0) + 1);
  }
  for (const kind of currentKinds) {
    const previousCount = previousCounts.get(kind) ?? 0;
    if (previousCount === 0) {
      return kind;
    }
    previousCounts.set(kind, previousCount - 1);
  }
  return null;
}

function renderTreasureNotifications(
  stack: HTMLElement,
  spawnNotice: TreasureNotice | null,
  toasts: TreasureNotice[],
): void {
  const now = Date.now();
  const visibleToasts = toasts.filter((notice) => notice.expiresAt > now);
  toasts.splice(0, toasts.length, ...visibleToasts);
  const notices = [
    spawnNotice && (spawnNotice.persistent || spawnNotice.expiresAt > now)
      ? spawnNotice
      : null,
    ...visibleToasts,
  ].filter((notice): notice is TreasureNotice => notice !== null);

  const signature = notices
    .map((notice) => `${notice.id}:${notice.persistent ? "1" : "0"}`)
    .join("|");
  if (stack.dataset.noticeSignature === signature) {
    return;
  }
  stack.dataset.noticeSignature = signature;
  stack.innerHTML = notices.map(renderTreasureNotice).join("");
}

function renderTreasureNotice(notice: TreasureNotice): string {
  const icon = notice.iconPath
    ? `<img class="treasure-notice-icon" src="${notice.iconPath}" alt="" style="--treasure-glow:${notice.glow}">`
    : `<span class="treasure-notice-icon is-text">${escapeHtml(formatTreasureNoticeIconText(currentLocale))}</span>`;
  if (notice.treasureName) {
    return `
      <div class="treasure-notice is-treasure" data-notice-id="${escapeHtml(notice.id)}">
        <strong class="treasure-notice-title">${escapeHtml(notice.title)}</strong>
        ${icon}
        <span class="treasure-notice-name">${escapeHtml(notice.treasureName)}</span>
        <small class="treasure-notice-effect">${escapeHtml(notice.body)}</small>
      </div>
    `;
  }
  return `
    <div class="treasure-notice" data-notice-id="${escapeHtml(notice.id)}">
      ${icon}
      <span class="treasure-notice-copy">
        <strong>${escapeHtml(notice.title)}</strong>
        <small>${escapeHtml(notice.body)}</small>
      </span>
    </div>
  `;
}

function stopGameInputPropagation(element: HTMLElement): void {
  for (const eventName of [
    "pointerdown",
    "pointerup",
    "pointermove",
    "click",
  ]) {
    element.addEventListener(eventName, (event) => {
      event.stopPropagation();
    });
  }
}

function installAscensionRitualOverlay(store: RuntimeStateStore): void {
  const shell = document.querySelector<HTMLElement>("#game-shell");
  if (!shell) {
    return;
  }

  const overlay = document.createElement("div");
  overlay.id = "ascension-ritual";
  overlay.className = "ascension-ritual is-hidden";
  overlay.setAttribute("aria-live", "assertive");
  shell.append(overlay);
  let lastOverlaySignature: string | null = null;

  store.subscribe((state) => {
    const ritual = state.ascensionRitual;
    if (!ritual) {
      if (lastOverlaySignature !== null) {
        overlay.className = "ascension-ritual is-hidden";
        overlay.innerHTML = "";
        lastOverlaySignature = null;
      }
      return;
    }

    const now = Date.now();
    const rebornHideAt = ritual.rebornAt ? ritual.rebornAt + 4200 : null;
    if (rebornHideAt !== null && now >= rebornHideAt) {
      if (lastOverlaySignature !== null) {
        overlay.className = "ascension-ritual is-hidden";
        overlay.innerHTML = "";
        lastOverlaySignature = null;
      }
      return;
    }

    const remainingMs = Math.max(0, ritual.endsAt - now);
    const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
    const className = `ascension-ritual is-${ritual.phase}${ritual.isSelf ? " is-self" : ""}`;
    const html =
      ritual.phase === "reborn"
        ? renderWorldRebornOverlay(ritual.ascenderName)
        : renderAscensionOverlay({
            ascenderName: ritual.ascenderName,
            scoreGain: ritual.scoreGain,
            totalScore: ritual.totalScore,
            seconds,
            isSelf: ritual.isSelf,
          });
    const signature = [
      className,
      ritual.phase,
      ritual.ascenderId,
      ritual.ascenderName,
      ritual.scoreGain.toString(),
      ritual.totalScore.toString(),
      seconds.toString(),
    ].join("|");
    if (signature === lastOverlaySignature) {
      return;
    }
    lastOverlaySignature = signature;
    overlay.className = className;
    overlay.innerHTML = html;
  });
}

function renderAscensionOverlay(options: {
  ascenderName: string;
  scoreGain: number;
  totalScore: number;
  seconds: number;
  isSelf: boolean;
}): string {
  const copy = formatAscensionOverlay(currentLocale, options);
  return `
    <div class="ascension-ink"></div>
    <div class="ascension-card">
      <div class="ascension-seal">${escapeHtml(copy.seal)}</div>
      <div class="ascension-title">${escapeHtml(copy.title)}</div>
      <div class="ascension-subtitle">${escapeHtml(copy.subtitle)}</div>
      <div class="ascension-countdown">${options.seconds.toString()} ${escapeHtml(formatAscensionCountdownUnit(currentLocale))}</div>
    </div>
  `;
}

function renderWorldRebornOverlay(ascenderName: string): string {
  const copy = formatWorldRebornOverlay(currentLocale, ascenderName);
  return `
    <div class="ascension-ink"></div>
    <div class="ascension-card">
      <div class="ascension-seal">${escapeHtml(copy.seal)}</div>
      <div class="ascension-title">${escapeHtml(copy.title)}</div>
      <div class="ascension-subtitle">${escapeHtml(copy.subtitle)}</div>
    </div>
  `;
}

function updateLeaderboardPanel(
  leaderboard: HTMLElement,
  leaderboardPanel: HTMLElement | null,
  state: ReturnType<typeof createLeaderboardRenderState>,
  shouldRender: (html: string) => boolean = () => true,
): void {
  const html = renderLeaderboardPanel(state);
  if (shouldRender(html)) {
    leaderboard.innerHTML = html;
  }
  if (leaderboardPanel) {
    leaderboardPanel.hidden = html.trim().length === 0;
  }
}

function createLeaderboardRenderState(state: RuntimeViewState) {
  return {
    snapshot: state.lastSnapshot,
    activeLeaderboard: state.lastLeaderboard,
    periodLeaderboards: state.lastPeriodLeaderboards,
    selectedPeriod,
    showPeriodTabs: identityPromptActive,
    statusMessage: state.statusMessage,
    currentPlayerKey: state.welcome?.playerKey ?? readStoredPlayerKey(),
    currentPlayerName:
      state.lastSnapshot?.self.name ?? runtimeConfig.playerName,
    locale: currentLocale,
  };
}

function updateCollapsedLeaderboardSelf(
  container: HTMLElement | null,
  levelElement: HTMLElement | null,
  scoreElement: HTMLElement | null,
  spectatorElement: HTMLElement | null,
  state: RuntimeViewState,
): void {
  const level = readCurrentPlayerLevel(state);
  const score = readCurrentLeaderboardScore(state);
  const spectatorCount = readCurrentPlayerSpectatorCount(state);

  if (levelElement) {
    if (level === null) {
      levelElement.hidden = true;
      levelElement.textContent = "";
    } else {
      levelElement.textContent = formatRealmLayerCompact(currentLocale, level);
      levelElement.hidden = false;
    }
  }

  if (scoreElement) {
    if (score === null) {
      scoreElement.hidden = true;
      scoreElement.textContent = "";
    } else {
      scoreElement.textContent = formatScore(score, currentLocale);
      scoreElement.hidden = false;
    }
  }

  if (spectatorElement) {
    if (spectatorCount <= 0) {
      spectatorElement.hidden = true;
      spectatorElement.textContent = "";
    } else {
      spectatorElement.textContent = `👁 ${spectatorCount.toString()}`;
      spectatorElement.hidden = false;
    }
  }

  if (container) {
    container.hidden = level === null && score === null && spectatorCount <= 0;
  }
}

function readCurrentPlayerLevel(state: RuntimeViewState): number | null {
  const level = state.lastSnapshot?.self.level;
  return typeof level === "number" ? level : null;
}

function readCurrentLeaderboardScore(state: RuntimeViewState): number | null {
  const selfId = state.lastSnapshot?.self.id;
  if (selfId) {
    const selfEntry = state.lastLeaderboard.find(
      (entry) => entry.id === selfId,
    );
    if (typeof selfEntry?.score === "number") {
      return selfEntry.score;
    }
  }
  // Fall back to the settled score so the readout survives the restart screen
  // after death (when the player has left the live board).
  return state.lastSettledScore?.score ?? null;
}

function readCurrentPlayerSpectatorCount(state: RuntimeViewState): number {
  const selfId = state.lastSnapshot?.self.id;
  if (!selfId) {
    return 0;
  }
  const fromSnapshot = state.lastSnapshot?.self.spectatorCount;
  if (typeof fromSnapshot === "number") {
    return fromSnapshot;
  }
  const selfEntry = state.lastLeaderboard.find((entry) => entry.id === selfId);
  return typeof selfEntry?.spectatorCount === "number"
    ? selfEntry.spectatorCount
    : 0;
}

function isPeriodLeaderboardKind(
  value: string | undefined,
): value is PeriodLeaderboardKind {
  return value === "day" || value === "week" || value === "month";
}

function readStoredLeaderboardPeriod(): PeriodLeaderboardKind {
  try {
    const value =
      window.localStorage.getItem(LEADERBOARD_PERIOD_STORAGE_KEY) ?? undefined;
    return isPeriodLeaderboardKind(value) ? value : "day";
  } catch {
    return "day";
  }
}

function persistLeaderboardPeriod(period: PeriodLeaderboardKind): void {
  try {
    window.localStorage.setItem(LEADERBOARD_PERIOD_STORAGE_KEY, period);
  } catch {
    runtimeStore.recordLog(
      formatRuntimeLog(currentLocale, "leaderboardStorageUnavailable"),
      currentLocale,
    );
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
